import React, { useEffect, useMemo, useState } from "react"
import GroupPendingInvitesPanel from "./GroupPendingInvitesPanel"
import GroupChamaaCyclePanel from "./GroupChamaaCyclePanel"
import GroupMemberFinancePanel from "./GroupMemberFinancePanel"
import GroupChamaaSimulatorPanel from "./GroupChamaaSimulatorPanel"
import GroupSummaryCard from "./GroupSummaryCard"
import GroupWelfareSettingsPanel from "./GroupWelfareSettingsPanel"
import GroupWelfareLossReportPanel from "./GroupWelfareLossReportPanel"
import GroupWelfareOnboardingModal from "./GroupWelfareOnboardingModal"
import GroupJoinRequestsPanel from "./GroupJoinRequestsPanel"
import GroupNonRecurringPlaceholderPanel from "./GroupNonRecurringPlaceholderPanel"
import GroupArchiveConfirmModal from "./GroupArchiveConfirmModal"
import { Users, Wallet, Repeat, Trophy, AlertTriangle, Settings, ChevronDown, ChevronRight, Check } from "lucide-react"
import { formatDateLong, formatMoney, toNairobiDate } from "../../utils/format"
import api from "../../utils/api"
import { getGroupTheme } from "../../utils/groupTheme"
import { getApiErrorMessage } from "../../utils/errorMessage"

const NEXT_CYCLE_FREQUENCY_OPTIONS = [
  "Daily",
  "Bi-Daily",
  "Weekly",
  "Bi-Weekly",
  "Monthly",
  "Bi-Monthly",
  "Quarterly",
  "Bi-Annually",
  "Annually",
]

const ADMIN_TITLE_OPTIONS = [
  { value: "chair", label: "Chair" },
  { value: "treasurer", label: "Treasurer" },
  { value: "overseer", label: "Overseer" },
]

const ADMIN_TITLE_LABELS = {
  secretary: "Secretary",
  chair: "Chair",
  treasurer: "Treasurer",
  overseer: "Overseer",
}
const PEOPLE_VISIBLE_BATCH_SIZE = 5

const resolveAdminTitleLabel = (adminTitle) =>
  ADMIN_TITLE_LABELS[String(adminTitle || "").toLowerCase()] || "Admin"

const getAdminTitleBadgeClass = (adminTitle) => {
  const key = String(adminTitle || "").toLowerCase()
  if (key === "secretary") return "border-violet-300 bg-violet-100 text-violet-900"
  if (key === "treasurer") return "border-emerald-300 bg-emerald-100 text-emerald-900"
  if (key === "chair") return "border-amber-300 bg-amber-100 text-amber-900"
  if (key === "overseer") return "border-sky-300 bg-sky-100 text-sky-900"
  return "border-gray-300 bg-gray-100 text-gray-800"
}

const normalizeEditableAdminTitle = (adminTitle) => {
  const key = String(adminTitle || "").toLowerCase()
  return ADMIN_TITLE_OPTIONS.some((option) => option.value === key) ? key : "none"
}

const OLD_CYCLE_INVOICE_STATUS_CLASS = {
  paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  overdue: "bg-red-100 text-red-700 border-red-200",
  cancelled: "bg-gray-100 text-gray-600 border-gray-200",
}

const buildRoundWinnerRowsFromCycle = (cycle) => {
  if (!cycle) return []
  const rounds = Array.isArray(cycle?.rounds) ? cycle.rounds : []
  const orderedRounds = [...rounds].sort(
    (a, b) => Number(a?.roundNumber || 0) - Number(b?.roundNumber || 0)
  )

  return orderedRounds.map((round) => {
    const winnerName = round?.winnerName || ""
    const winnerSelectionNote = String(round?.winnerSelectionNote || "")
    const winnerId = String(round?.winnerId || "")
    const roundStatus = String(round?.status || "").toLowerCase()
    const winnerDisplayName = winnerName || winnerSelectionNote || "Pending recipient draw"
    const roundDate = (() => {
      if (roundStatus === "invoice_issued") {
        return (
          round?.payoutScheduledAt ||
          round?.invoiceDueDate ||
          round?.invoiceScheduledAt ||
          round?.invoiceIssuedAt ||
          null
        )
      }
      if (roundStatus === "payout_processed") {
        return (
          round?.payoutProcessedAt ||
          round?.payoutScheduledAt ||
          round?.invoiceScheduledAt ||
          round?.invoiceIssuedAt ||
          null
        )
      }
      return round?.invoiceScheduledAt || round?.invoiceIssuedAt || null
    })()
    return {
      key: `${cycle?.id || cycle?._id || cycle?.sequenceNumber}-${round?.roundNumber}`,
      cycleSequence: Number(cycle?.sequenceNumber || 0),
      cycleStatus: String(cycle?.status || ""),
      roundNumber: Number(round?.roundNumber || 0),
      winnerName: winnerDisplayName,
      winnerId,
      winnerSelectionNote,
      status: roundStatus,
      roundDate,
      invoiceScheduledAt: round?.invoiceScheduledAt || null,
      invoiceIssuedAt: round?.invoiceIssuedAt || null,
      invoiceDueDate: round?.invoiceDueDate || null,
      payoutScheduledAt: round?.payoutScheduledAt || null,
      payoutProcessedAt: round?.payoutProcessedAt || null,
      invoiceCount: Number(round?.invoiceCount || 0),
      paidInvoiceCount: Number(round?.paidInvoiceCount || 0),
      expectedPayoutAmount: Number(round?.expectedPayoutAmount || 0),
      actualPayoutAmount: Number(round?.actualPayoutAmount || 0),
    }
  })
}

const renderDateWithYearBreak = (value) => {
  const formatted = String(formatDateLong(value) || "").trim()
  if (!formatted) return "-"
  const parts = formatted.split(" ")
  if (parts.length < 2) return formatted
  const year = parts.pop()
  const main = parts.join(" ")
  return (
    <span className="inline-flex flex-col leading-tight whitespace-normal">
      <span className="whitespace-nowrap">{main}</span>
      <span className="text-[9px] text-gray-500">{year}</span>
    </span>
  )
}

const isInsufficientGroupsWalletMessage = (message) => {
  const normalized = String(message || "").toLowerCase()
  return normalized.includes("insufficient") && normalized.includes("wallet")
}

const parseSafeDate = (value) => {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const formatHoursLeftUntil = (value, referenceNow = new Date()) => {
  const target = toNairobiDate(value)
  const now = toNairobiDate(referenceNow)
  if (!target || !now) return ""
  const diffMs = target.getTime() - now.getTime()
  if (diffMs <= 0) return "Payout window open"
  const totalMinutes = Math.ceil(diffMs / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  if (hours <= 0) return `${minutes}m left till payout`
  if (minutes === 0) return `${hours}h left till payout`
  return `${hours}h ${minutes}m left till payout`
}

const getWelfareLossApprovalStatusMeta = (status) => {
  const normalized = String(status || "").toLowerCase()
  if (normalized === "approved") {
    return {
      label: "Approved",
      className: "bg-emerald-50 border-emerald-200 text-emerald-700",
    }
  }
  if (normalized === "rejected") {
    return {
      label: "Rejected",
      className: "bg-rose-50 border-rose-200 text-rose-700",
    }
  }
  if (normalized === "cancelled") {
    return {
      label: "Cancelled",
      className: "bg-gray-50 border-gray-200 text-gray-600",
    }
  }
  return {
    label: "Pending",
    className: "bg-amber-50 border-amber-200 text-amber-700",
  }
}

const normalizeGroupWelfareProbationDuration = (probation) => {
  const value = Number(probation?.value || 0)
  const unit = String(probation?.unit || "").trim().toLowerCase()
  if (!Number.isInteger(value) || value <= 0) return null
  if (!["days", "months"].includes(unit)) return null
  return { value, unit }
}

const shiftDateByProbationDuration = (date, probation, direction = 1) => {
  if (!date || Number.isNaN(date.getTime()) || !probation) return null
  const next = new Date(date)
  const delta = Number(direction || 1) * Number(probation.value || 0)
  if (probation.unit === "days") next.setDate(next.getDate() + delta)
  else next.setMonth(next.getMonth() + delta)
  return next
}

const resolveWelfareMemberProbationDisplay = ({ member, group, probationDuration }) => {
  const now = new Date()
  const status = String(member?.welfareStatus || "").toLowerCase()
  const probationReason = String(member?.welfareProbationReason || "").trim().toLowerCase()
  const latePaymentEnd = parseSafeDate(member?.welfareProbationEndsAt)
  const latePaymentStartRaw = parseSafeDate(member?.welfareProbationStartedAt)
  const latePaymentStart =
    latePaymentStartRaw ||
    (latePaymentEnd && probationDuration
      ? shiftDateByProbationDuration(latePaymentEnd, probationDuration, -1)
      : null)

  const registrationEligibleAt = parseSafeDate(member?.registrationEligibleAt)
  const registrationInvoicedAt = parseSafeDate(member?.registrationInvoicedAt)
  const isEnrollmentProbation =
    !!registrationEligibleAt &&
    registrationEligibleAt.getTime() > now.getTime() &&
    !registrationInvoicedAt
  if (isEnrollmentProbation) {
    const enrollmentEnd = parseSafeDate(group?.welfare?.enrollment?.endDate)
    const startAt =
      enrollmentEnd ||
      (registrationEligibleAt && probationDuration
        ? shiftDateByProbationDuration(registrationEligibleAt, probationDuration, -1)
        : null)
    return {
      scenarioLabel: "Enrollment probation",
      startAt,
      endAt: registrationEligibleAt,
    }
  }

  const isProbation =
    status === "probation" && (!latePaymentEnd || latePaymentEnd.getTime() > now.getTime())
  if (!isProbation) return null

  const scenarioLabel =
    probationReason === "late_payment"
      ? "Late-payment probation"
      : probationReason === "join"
        ? "Joining probation"
        : "Probation"

  return {
    scenarioLabel,
    startAt: latePaymentStart,
    endAt:
      latePaymentEnd ||
      (latePaymentStart && probationDuration
        ? shiftDateByProbationDuration(latePaymentStart, probationDuration, 1)
        : null),
  }
}

function GroupDetails({
  group,
  createError,
  onOpenInviteModal,
  pendingInvites = [],
  pendingInvitesLoading = false,
  pendingInvitesError = "",
  onStartChamaaCycle,
  onPauseChamaaLifecycle,
  onResumeChamaaLifecycle,
  onStopChamaaLifecycle,
  onUpdateChamaaMaxShares,
  onUpdateChamaaMyShares,
  onUpdateNextCycleMember,
  onUpdateNextCycleSettings,
  onUpdateGroupSimulator,
  onAdvanceGroupSimulator,
  onUpdateWelfareSettings,
  onUpdateGroupJoinApproval,
  onContributeNonRecurring,
  onAddWelfareChild,
  onPromoteGroupAdmin,
  onUpdateAdminTitle,
  onRequestAdminLeave,
  onRequestAdminRemoval,
  onRequestSecretaryTransfer,
  onApproveAdminRequest,
  onRejectAdminRequest,
  onImpersonateMember,
  onGroupUpdated,
  isStartingChamaaCycle = false,
  isPausingChamaaLifecycle = false,
  isResumingChamaaLifecycle = false,
  isStoppingChamaaLifecycle = false,
  isUpdatingChamaaMaxShares = false,
  isUpdatingChamaaMyShares = false,
  isUpdatingNextCycleMember = false,
  isUpdatingNextCycleSettings = false,
  isUpdatingGroupSimulator = false,
  isAdvancingGroupSimulator = false,
  isUpdatingWelfareSettings = false,
  isUpdatingGroupJoinApproval = false,
  isContributingNonRecurring = false,
  isAddingWelfareChild = false,
  isUpdatingAdminWorkflow = false,
  isGroupScopedImpersonation = false,
  isArchivedReadOnly = false,
  onReactivateArchivedGroup,
  isReactivatingArchivedGroup = false,
}) {
  const isAdmin = group?.myRole === "admin"
  const isSecretary = !!group?.isSecretary || String(group?.myAdminTitle || "").toLowerCase() === "secretary"
  const isChamaa = !!group?.isChamaa
  const isProjectSavings = !!group?.isProjectSavings
  const isWelfare = !!group?.isWelfare
  const isNonRecurring = String(group?.category || "").toLowerCase() === "non-recurring"
  const isMemberChamaa = isChamaa && !isAdmin
  const isWelfareEnrollmentOpen = !!(group?.welfare?.isEnrollmentOpen || group?.welfare?.enrollment?.isEnrollmentOpen)
  const myMemberId = String(group?.myMemberId || "")
  const theme = getGroupTheme(group)
  const archivedReadOnlyMessage =
    "This group is archived in your personal view. Reactivate it to make changes."
  const joinApprovalMeta = group?.joinApproval || {}
  const isRecurringGroup = String(group?.category || "").toLowerCase() === "recurring"
  const isJoinApprovalEditable = !isRecurringGroup && joinApprovalMeta?.editable !== false
  const isJoinApprovalRequired = isRecurringGroup ? true : joinApprovalMeta?.required !== false
  const myMemberEntry = useMemo(
    () =>
      Array.isArray(group?.members)
        ? group.members.find((member) => String(member?.id || "") === myMemberId) || null
        : null,
    [group?.members, myMemberId]
  )
  const requiresWelfareOnboarding =
    isWelfare &&
    !!myMemberEntry?.welfareOnboardingRequired &&
    !myMemberEntry?.welfareOnboardingCompletedAt
  const welfareProbationDuration = normalizeGroupWelfareProbationDuration(
    group?.welfare?.probation || null
  )

  const tabs = useMemo(
    () =>
      isMemberChamaa
        ? [
          { id: "current-cycle", label: "Cycles", icon: Wallet },
          { id: "next-cycle", label: "Next Cycle", icon: Repeat },
        ]
        : isWelfare
          ? [
            ...(isAdmin ? [{ id: "admin-requests", label: "Approvals", icon: AlertTriangle }] : []),
            { id: "settings", label: "Settings", icon: Settings },
            { id: "current", label: "Current", icon: Wallet },
            { id: "people", label: "Members", icon: Users },
          ]
        : isChamaa
          ? [
              ...(isAdmin ? [{ id: "admin-requests", label: "Approvals", icon: AlertTriangle }] : []),
              { id: "current-cycle", label: "Cycles", icon: Wallet },
              { id: "next-cycle", label: "Next Cycle", icon: Repeat },
              { id: "people", label: "Members", icon: Users },
              { id: "round-winners", label: "Round Recipients", icon: Trophy },
            ]
          : [
              ...(isAdmin ? [{ id: "admin-requests", label: "Approvals", icon: AlertTriangle }] : []),
              { id: "current-cycle", label: isNonRecurring ? "Current" : "Current Cycle", icon: Wallet },
              { id: "people", label: "Members (Invite)", icon: Users },
            ],
    [isMemberChamaa, isWelfare, isChamaa, isAdmin, isNonRecurring]
  )

  const defaultTabId =
    isWelfare
      ? "current"
      : tabs.some((tab) => tab.id === "current-cycle")
        ? "current-cycle"
        : (tabs[0]?.id || "current-cycle")

  const [activeTab, setActiveTab] = useState(defaultTabId)
  const [oldCycles, setOldCycles] = useState([])
  const [oldCyclesLoading, setOldCyclesLoading] = useState(false)
  const [oldCyclesError, setOldCyclesError] = useState("")
  const [allCycles, setAllCycles] = useState([])
  const [winnerCycleFilter, setWinnerCycleFilter] = useState("all")
  const [winnerStatusFilter, setWinnerStatusFilter] = useState("all")
  const [winnerSearch, setWinnerSearch] = useState("")
  const [nextCycleSharesDraft, setNextCycleSharesDraft] = useState({})
  const [nextCycleFrequencyDraft, setNextCycleFrequencyDraft] = useState("Weekly")
  const [nextCycleAmountDraft, setNextCycleAmountDraft] = useState("")
  const [nextCycleMaxSharesDraft, setNextCycleMaxSharesDraft] = useState("1")
  const [lateFeeDraft, setLateFeeDraft] = useState("0")
  const [nextCycleError, setNextCycleError] = useState("")
  const [nextCycleSuccess, setNextCycleSuccess] = useState("")
  const [nextCycleActionModal, setNextCycleActionModal] = useState(null)
  const [adminTitleDraftByMember, setAdminTitleDraftByMember] = useState({})
  const [adminWorkflowError, setAdminWorkflowError] = useState("")
  const [adminWorkflowSuccess, setAdminWorkflowSuccess] = useState("")
  const [welfareLossApprovalRequests, setWelfareLossApprovalRequests] = useState([])
  const [welfareLossApprovalLoading, setWelfareLossApprovalLoading] = useState(false)
  const [welfareLossApprovalError, setWelfareLossApprovalError] = useState("")
  const [isUpdatingWelfareLossApproval, setIsUpdatingWelfareLossApproval] = useState(false)
  const [welfareLossApprovalVisibleCount, setWelfareLossApprovalVisibleCount] = useState(3)
  const [welfareLossDecisionModal, setWelfareLossDecisionModal] = useState(null)
  const [welfareLossDecisionNote, setWelfareLossDecisionNote] = useState("")
  const [welfareLossDecisionError, setWelfareLossDecisionError] = useState("")
  const [isProbationMembersOpen, setIsProbationMembersOpen] = useState(false)
  const [isSuspensionMembersOpen, setIsSuspensionMembersOpen] = useState(false)
  const [probationMembersVisibleCount, setProbationMembersVisibleCount] = useState(5)
  const [suspensionMembersVisibleCount, setSuspensionMembersVisibleCount] = useState(5)
  const [pendingJoinRequestsCount, setPendingJoinRequestsCount] = useState(0)
  const [peopleSearch, setPeopleSearch] = useState("")
  const [peopleVisibleCount, setPeopleVisibleCount] = useState(PEOPLE_VISIBLE_BATCH_SIZE)
  const [pendingHistoricalInvoicePayment, setPendingHistoricalInvoicePayment] = useState(null)
  const [historicalPaymentAmountInput, setHistoricalPaymentAmountInput] = useState("")
  const [historicalProxyPaymentConfirmed, setHistoricalProxyPaymentConfirmed] = useState(false)
  const [isPayingHistoricalInvoiceId, setIsPayingHistoricalInvoiceId] = useState("")
  const [historicalPaymentError, setHistoricalPaymentError] = useState("")
  const [historicalPaymentSuccess, setHistoricalPaymentSuccess] = useState("")
  const [historicalCycleWinnersModal, setHistoricalCycleWinnersModal] = useState(null)
  const [historicalWinnersSearch, setHistoricalWinnersSearch] = useState("")
  const [joinApprovalError, setJoinApprovalError] = useState("")
  const [joinApprovalSuccess, setJoinApprovalSuccess] = useState("")
  const [reactivateArchivedGroupModalOpen, setReactivateArchivedGroupModalOpen] = useState(false)

  useEffect(() => {
    const allowed = new Set(tabs.map((t) => t.id))
    if (!allowed.has(activeTab)) {
      setActiveTab(defaultTabId)
    }
  }, [activeTab, tabs, defaultTabId])

  useEffect(() => {
    setActiveTab(defaultTabId)
  }, [group?.id, defaultTabId])

  useEffect(() => {
    let cancelled = false
    const loadPendingJoinRequestsCount = async () => {
      if (!group?.id || !isAdmin) {
        if (!cancelled) setPendingJoinRequestsCount(0)
        return
      }
      try {
        const res = await api.get(`/groups/${group.id}/join-requests/pending`)
        const rows = Array.isArray(res?.data?.requests) ? res.data.requests : []
        if (!cancelled) setPendingJoinRequestsCount(rows.length)
      } catch (_) {
        if (!cancelled) setPendingJoinRequestsCount(0)
      }
    }
    loadPendingJoinRequestsCount()
    return () => {
      cancelled = true
    }
  }, [group?.id, isAdmin])

  const buildHistoricalCycles = (cycles) => {
    if (!Array.isArray(cycles)) return []
    if (isMemberChamaa) {
      const memberId = String(group?.myMemberId || "")
      const participated = memberId
        ? cycles.filter(
            (cycle) =>
              Array.isArray(cycle?.participants) &&
              cycle.participants.some((participant) => String(participant?.id || "") === memberId)
          )
        : cycles
      return participated
        .filter((cycle) => !["scheduled", "active"].includes(String(cycle?.status || "").toLowerCase()))
        .sort((a, b) => Number(b?.sequenceNumber || 0) - Number(a?.sequenceNumber || 0))
    }

    return cycles
      .filter((cycle) => !["scheduled", "active"].includes(String(cycle?.status || "").toLowerCase()))
      .sort((a, b) => Number(b?.sequenceNumber || 0) - Number(a?.sequenceNumber || 0))
  }

  const applyCyclesData = (cycles) => {
    setAllCycles(Array.isArray(cycles) ? cycles : [])
    setOldCycles(buildHistoricalCycles(cycles))
  }

  const refreshCyclesData = async () => {
    if (!isChamaa || !group?.id) return
    const res = await api.get(`/groups/${group.id}/cycles`)
    const cycles = Array.isArray(res?.data?.cycles) ? res.data.cycles : []
    applyCyclesData(cycles)
  }

  useEffect(() => {
    if (!isChamaa || !group?.id) return
    const shouldLoad =
      activeTab === "current-cycle" ||
      activeTab === "cycles" ||
      activeTab === "round-winners"
    if (!shouldLoad) return
    let cancelled = false

    const loadCycles = async () => {
      setOldCyclesLoading(true)
      setOldCyclesError("")
      try {
        const res = await api.get(`/groups/${group.id}/cycles`)
        const cycles = Array.isArray(res?.data?.cycles) ? res.data.cycles : []
        if (cancelled) return
        applyCyclesData(cycles)
      } catch (err) {
        const message = getApiErrorMessage(err, "Failed to load old cycles.")
        if (!cancelled) {
          setAllCycles([])
          setOldCycles([])
          setOldCyclesError(message)
        }
      } finally {
        if (!cancelled) setOldCyclesLoading(false)
      }
    }

    loadCycles()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, group?.id, group?.myMemberId, isMemberChamaa, isChamaa])

  const cycleFilterOptions = useMemo(() => {
    return [...(allCycles || [])].sort(
      (a, b) => Number(b?.sequenceNumber || 0) - Number(a?.sequenceNumber || 0)
    )
  }, [allCycles])
  const historicalCyclesWithSortedInvoices = useMemo(() => {
    return (oldCycles || []).map((cycle) => ({
      ...cycle,
      outstandingInvoices: [...(Array.isArray(cycle?.outstandingInvoices) ? cycle.outstandingInvoices : [])].sort(
        (a, b) => {
          const aIsMine = myMemberId && String(a?.memberId || "") === myMemberId
          const bIsMine = myMemberId && String(b?.memberId || "") === myMemberId
          if (aIsMine && !bIsMine) return -1
          if (!aIsMine && bIsMine) return 1
          const roundDiff = Number(a?.roundNumber || 0) - Number(b?.roundNumber || 0)
          if (roundDiff !== 0) return roundDiff
          const aDate = new Date(a?.dueDate || a?.issuedAt || a?.createdAt || 0).getTime()
          const bDate = new Date(b?.dueDate || b?.issuedAt || b?.createdAt || 0).getTime()
          return aDate - bDate
        }
      ),
    }))
  }, [oldCycles, myMemberId])
  const historicalOutstandingCycles = useMemo(() => {
    return historicalCyclesWithSortedInvoices.filter((cycle) => cycle.outstandingInvoices.length > 0)
  }, [historicalCyclesWithSortedInvoices])
  const historicalOutstandingTotals = useMemo(() => {
    return historicalOutstandingCycles.reduce(
      (summary, cycle) => ({
        cycleCount: summary.cycleCount + 1,
        invoiceCount: summary.invoiceCount + Number(cycle?.outstandingInvoiceCount || 0),
        amount: summary.amount + Number(cycle?.outstandingAmount || 0),
      }),
      { cycleCount: 0, invoiceCount: 0, amount: 0 }
    )
  }, [historicalOutstandingCycles])
  const selectedHistoricalWinnersCycle = useMemo(() => {
    if (!historicalCycleWinnersModal) return null
    const modalCycleKey = String(historicalCycleWinnersModal?.cycleKey || "")
    const cycleSource = [...(oldCycles || []), ...(allCycles || [])]
    return (
      cycleSource.find(
        (cycle) =>
          String(cycle?.id || cycle?._id || cycle?.sequenceNumber || "") === modalCycleKey
      ) || null
    )
  }, [historicalCycleWinnersModal, oldCycles, allCycles])
  const selectedHistoricalCycleWinnerRows = useMemo(
    () => buildRoundWinnerRowsFromCycle(selectedHistoricalWinnersCycle),
    [selectedHistoricalWinnersCycle]
  )
  const filteredHistoricalCycleWinnerRows = useMemo(() => {
    const q = String(historicalWinnersSearch || "").trim().toLowerCase()
    if (!q) return selectedHistoricalCycleWinnerRows
    return selectedHistoricalCycleWinnerRows.filter((row) =>
      String(row?.winnerName || "").toLowerCase().includes(q)
    )
  }, [historicalWinnersSearch, selectedHistoricalCycleWinnerRows])
  const oldestOutstandingInvoiceByMember = useMemo(() => {
    const byMember = new Map()
    const outstandingInvoices = []

    for (const cycle of allCycles || []) {
      const cycleSequence = Number(cycle?.sequenceNumber || 0)
      for (const invoice of cycle?.outstandingInvoices || []) {
        const balanceAmount = Number(invoice?.balanceAmount || 0)
        const status = String(invoice?.status || "").toLowerCase()
        if (!(balanceAmount > 0) || ["paid", "cancelled"].includes(status)) continue
        outstandingInvoices.push({
          ...invoice,
          cycleSequence,
        })
      }
    }

    outstandingInvoices.sort((a, b) => {
      const aDue = new Date(a?.dueDate || a?.createdAt || 0).getTime()
      const bDue = new Date(b?.dueDate || b?.createdAt || 0).getTime()
      if (aDue !== bDue) return aDue - bDue
      const aCycle = Number(a?.cycleSequence || 0)
      const bCycle = Number(b?.cycleSequence || 0)
      if (aCycle !== bCycle) return aCycle - bCycle
      const roundDiff = Number(a?.roundNumber || 0) - Number(b?.roundNumber || 0)
      if (roundDiff !== 0) return roundDiff
      return new Date(a?.createdAt || 0).getTime() - new Date(b?.createdAt || 0).getTime()
    })

    for (const invoice of outstandingInvoices) {
      const memberId = String(invoice?.memberId || "")
      if (!memberId || byMember.has(memberId)) continue
      byMember.set(memberId, invoice)
    }

    return byMember
  }, [allCycles])
  const historicalPaymentValidationMessage = useMemo(() => {
    if (!pendingHistoricalInvoicePayment) return ""
    const raw = String(historicalPaymentAmountInput || "").trim()
    if (!raw) return ""

    const amount = Number(raw)
    const maxAmount = Number(pendingHistoricalInvoicePayment?.balanceAmount || 0)
    if (!Number.isFinite(amount)) return "Enter a valid payment amount."
    if (amount < 0) return "Payment amount cannot be negative."
    if (amount === 0) return "Payment amount must be greater than 0."
    if (amount > maxAmount) {
      return `Payment amount cannot be greater than ${formatMoney(maxAmount)}.`
    }
    if (isChamaa && Math.round(amount * 100) !== Math.round(maxAmount * 100)) {
      return "Chamaa invoice payments must settle the full oldest unpaid invoice."
    }
    return ""
  }, [historicalPaymentAmountInput, isChamaa, pendingHistoricalInvoicePayment])

  const closePendingHistoricalPaymentModal = () => {
    if (String(isPayingHistoricalInvoiceId || "") === String(pendingHistoricalInvoicePayment?.id || "")) return
    setPendingHistoricalInvoicePayment(null)
    setHistoricalPaymentAmountInput("")
    setHistoricalProxyPaymentConfirmed(false)
  }
  const closeHistoricalCycleWinnersModal = () => {
    setHistoricalCycleWinnersModal(null)
    setHistoricalWinnersSearch("")
  }

  const handleRequestHistoricalInvoicePayment = (invoice) => {
    if (!invoice?.id) return
    setHistoricalPaymentError("")
    setHistoricalPaymentSuccess("")

    const invoiceMemberId = String(invoice?.memberId || "")
    const oldestOutstandingInvoice = oldestOutstandingInvoiceByMember.get(invoiceMemberId)
    if (
      isChamaa &&
      oldestOutstandingInvoice &&
      String(oldestOutstandingInvoice?.id || "") !== String(invoice?.id || "")
    ) {
      setHistoricalPaymentError(
        `You must first pay for Round ${oldestOutstandingInvoice?.roundNumber || "-"}.`
      )
      return
    }

    setPendingHistoricalInvoicePayment(invoice)
    setHistoricalPaymentAmountInput(String(Number(invoice?.balanceAmount || 0) || ""))
    setHistoricalProxyPaymentConfirmed(false)
  }

  const handleConfirmHistoricalInvoicePayment = async () => {
    if (!group?.id || !pendingHistoricalInvoicePayment?.id) return
    const invoice = pendingHistoricalInvoicePayment
    setHistoricalPaymentError("")
    setHistoricalPaymentSuccess("")

    const currentBalance = Number(invoice?.balanceAmount || 0)
    const amount = Number(historicalPaymentAmountInput)
    if (!(currentBalance > 0)) {
      setHistoricalPaymentError("Invoice balance must be greater than 0.")
      return
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setHistoricalPaymentError("Payment amount must be greater than 0.")
      return
    }
    if (amount > currentBalance) {
      setHistoricalPaymentError("Payment amount cannot be greater than the outstanding balance.")
      return
    }
    if (isChamaa && Math.round(amount * 100) !== Math.round(currentBalance * 100)) {
      setHistoricalPaymentError("Chamaa invoice payments must settle the full oldest unpaid invoice.")
      return
    }

    setIsPayingHistoricalInvoiceId(String(invoice.id))
    try {
      const res = await api.post(`/groups/${group.id}/invoices/${invoice.id}/pay`, { amount })
      const updatedGroup = res?.data?.group
      if (updatedGroup?.id) onGroupUpdated?.(updatedGroup)
      try {
        await refreshCyclesData()
      } catch (refreshErr) {
        const refreshMessage = getApiErrorMessage(
          refreshErr,
          "Payment succeeded but failed to refresh cycles."
        )
        setOldCyclesError(refreshMessage)
      }
      setHistoricalPaymentSuccess("Invoice paid successfully.")
      setPendingHistoricalInvoicePayment(null)
      setHistoricalPaymentAmountInput("")
      setHistoricalProxyPaymentConfirmed(false)
    } catch (err) {
      const message = getApiErrorMessage(err, "Failed to pay invoice.")
      if (isInsufficientGroupsWalletMessage(message)) {
        window.alert("Insufficient funds in your Groups Wallet. Please add funds and try again.")
      }
      setHistoricalPaymentError(message)
    } finally {
      setIsPayingHistoricalInvoiceId("")
    }
  }

  const roundWinnerRows = useMemo(() => {
    const cycleFilter = String(winnerCycleFilter || "all")
    const statusFilter = String(winnerStatusFilter || "all")
    const q = String(winnerSearch || "").trim().toLowerCase()

    const sourceCycles = (allCycles || [])
      .filter((cycle) =>
        cycleFilter === "all" ? true : String(cycle?.sequenceNumber || "") === cycleFilter
      )
      .sort((a, b) => Number(b?.sequenceNumber || 0) - Number(a?.sequenceNumber || 0))

    const rows = []
    for (const cycle of sourceCycles) {
      const cycleRows = buildRoundWinnerRowsFromCycle(cycle)
      for (const row of cycleRows) {
        const winnerDisplayName = row.winnerName
        const winnerId = row.winnerId
        const status = row.status
        const isDrawn = !!winnerId

        const statusMatch =
          statusFilter === "all" ||
          (statusFilter === "drawn" && isDrawn) ||
          (statusFilter === "paid_out" && status === "payout_processed") ||
          (statusFilter === "pending_draw" && !isDrawn)

        const searchMatch = !q || winnerDisplayName.toLowerCase().includes(q)
        if (!statusMatch || !searchMatch) continue

        rows.push(row)
      }
    }
    return rows
  }, [allCycles, winnerCycleFilter, winnerStatusFilter, winnerSearch])

  const winnerStats = useMemo(() => {
    const total = roundWinnerRows.length
    const drawnAndPaid = roundWinnerRows.filter((row) => row.status === "payout_processed").length
    const lateOrDefault = roundWinnerRows.filter((row) => {
      const hasWinner = !!row.winnerId
      const hasEligibilityOrDefaultNote = !!String(row.winnerSelectionNote || "").trim()
      const isUnpaidWinner = hasWinner && row.status !== "payout_processed"
      return isUnpaidWinner || hasEligibilityOrDefaultNote
    }).length
    const uniqueWinners = new Set(
      roundWinnerRows.map((row) => String(row.winnerId || "")).filter(Boolean)
    ).size
    return { total, drawnAndPaid, lateOrDefault, uniqueWinners }
  }, [roundWinnerRows])

  useEffect(() => {
    if (winnerCycleFilter === "all") return
    const exists = cycleFilterOptions.some(
      (cycle) => String(cycle?.sequenceNumber || "") === String(winnerCycleFilter)
    )
    if (!exists) {
      setWinnerCycleFilter("all")
    }
  }, [cycleFilterOptions, winnerCycleFilter])

  const getWinnerStatusMeta = (row) => {
    const value = String(row?.status || "").toLowerCase()
    const winnerSelectionNote = String(row?.winnerSelectionNote || "")
    const payoutAmount = Number(row?.actualPayoutAmount || row?.expectedPayoutAmount || 0)
    const invoiceCount = Number(row?.invoiceCount || 0)
    const paidInvoiceCount = Number(row?.paidInvoiceCount || 0)

    if (winnerSelectionNote) {
      return { label: winnerSelectionNote, className: "bg-red-100 text-red-700 border-red-200", detail: "" }
    }
    if (value === "payout_processed") {
      return {
        label: payoutAmount > 0 ? `Paid Out ${formatMoney(payoutAmount)}` : "Paid Out",
        className: "bg-emerald-100 text-emerald-700 border-emerald-200",
        detail: "",
      }
    }
    if (value === "invoice_issued") {
      const fullyFunded = invoiceCount > 0 && paidInvoiceCount >= invoiceCount
      const payoutCountdown = fullyFunded && row?.payoutScheduledAt
        ? formatHoursLeftUntil(row.payoutScheduledAt)
        : ""
      return {
        label: fullyFunded ? "Awaiting scheduled payout" : "Awaiting Funds",
        className: "bg-amber-100 text-amber-700 border-amber-200",
        detail: payoutCountdown,
      }
    }
    if (value === "scheduled") {
      return { label: "Scheduled", className: "bg-slate-100 text-slate-700 border-slate-200", detail: "" }
    }
    return { label: value || "-", className: "bg-gray-100 text-gray-700 border-gray-200", detail: "" }
  }

  const nextCycleMembers = useMemo(() => {
    const participants = Array.isArray(group?.nextCycle?.participants) ? group.nextCycle.participants : []
    const participantMap = new Map(
      participants.map((participant) => [String(participant?.id || ""), participant])
    )
    const currentMemberId = String(group?.myMemberId || "")
    const activeMembers = Array.isArray(group?.members)
      ? group.members.filter((member) => !member?.hasLeftGroup)
      : []

    return activeMembers
      .map((member) => {
        const memberId = String(member?.id || "")
        const participant = participantMap.get(memberId) || null
        const isLeavingAfterCurrentCycle = !!member?.leaveAfterCurrentCycle
        const isIncludedInNextCycle =
          !isLeavingAfterCurrentCycle &&
          (participant ? true : !!member?.includedInNextCycle)
        const isExplicitlySkipped = !isLeavingAfterCurrentCycle && !!member?.skipNextCycle
        const skipActionMode = isExplicitlySkipped ? "unskip" : "skip"
        const skipActionEnabled =
          !isLeavingAfterCurrentCycle &&
          (isIncludedInNextCycle || isExplicitlySkipped)

        let nextCycleStateLabel = "Included"
        if (isLeavingAfterCurrentCycle) nextCycleStateLabel = "Leaving"
        else if (isExplicitlySkipped) nextCycleStateLabel = "Skipped"
        else if (!isIncludedInNextCycle) nextCycleStateLabel = "Not included"

        return {
          id: memberId,
          name: participant?.name || member?.name || "Member",
          role: member?.role || "member",
          shareCount: Number(participant?.shareCount || member?.desiredShares || 1),
          hasOutstandingDebt: !!member?.hasOutstandingDebt,
          isIncludedInNextCycle,
          isExplicitlySkipped,
          isLeavingAfterCurrentCycle,
          skipActionMode,
          skipActionEnabled,
          nextCycleStateLabel,
          isMe: memberId === currentMemberId,
        }
      })
      .sort((a, b) => {
        if (a.isMe && !b.isMe) return -1
        if (!a.isMe && b.isMe) return 1
        return a.name.localeCompare(b.name)
      })
  }, [group?.members, group?.nextCycle?.participants, group?.myMemberId])
  const isNextCycleEditable = String(group?.nextCycle?.status || "").toLowerCase() === "scheduled"

  const peopleMembers = useMemo(() => {
    const activeMembers = Array.isArray(group?.members)
      ? group.members.map((member) => ({
          ...member,
          hasLeftGroup: false,
          leftAt: null,
        }))
      : []
    const archivedMembers = Array.isArray(group?.historyMembers)
      ? group.historyMembers.map((member) => ({
          ...member,
          hasLeftGroup: true,
        }))
      : []
    return [...activeMembers, ...archivedMembers].sort((a, b) => {
      const aId = String(a?.id || "")
      const bId = String(b?.id || "")
      const aIsMe = myMemberId && aId === myMemberId
      const bIsMe = myMemberId && bId === myMemberId
      if (aIsMe && !bIsMe) return -1
      if (!aIsMe && bIsMe) return 1
      if (!!a?.hasLeftGroup !== !!b?.hasLeftGroup) return a?.hasLeftGroup ? 1 : -1
      return String(a?.name || "").localeCompare(String(b?.name || ""))
    })
  }, [group?.members, group?.historyMembers, myMemberId])
  const activePeopleMembers = useMemo(
    () => peopleMembers.filter((member) => !member?.hasLeftGroup),
    [peopleMembers]
  )
  const filteredPeopleMembers = useMemo(() => {
    const query = String(peopleSearch || "").trim().toLowerCase()
    if (!query) return peopleMembers
    return peopleMembers.filter((member) =>
      String(member?.name || "").toLowerCase().includes(query)
    )
  }, [peopleMembers, peopleSearch])
  const visiblePeopleMembers = useMemo(
    () => filteredPeopleMembers.slice(0, peopleVisibleCount),
    [filteredPeopleMembers, peopleVisibleCount]
  )
  const canShowMorePeopleMembers = filteredPeopleMembers.length > peopleVisibleCount
  const welfareProbationMembers = useMemo(() => {
    if (!isWelfare) return []
    return activePeopleMembers
      .map((member) => {
        const probationDisplay = resolveWelfareMemberProbationDisplay({
          member,
          group,
          probationDuration: welfareProbationDuration,
        })
        const welfareStatus = String(member?.welfareStatus || "").toLowerCase()
        const isProbation = welfareStatus === "probation" || !!probationDisplay
        if (!isProbation) return null
        return {
          id: String(member?.id || ""),
          name: member?.name || "Member",
          welfareMemberIdentifier: String(member?.welfareMemberIdentifier || "").trim(),
          startAt: probationDisplay?.startAt || parseSafeDate(member?.welfareProbationStartedAt),
          endAt: probationDisplay?.endAt || parseSafeDate(member?.welfareProbationEndsAt),
        }
      })
      .filter(Boolean)
  }, [activePeopleMembers, group, isWelfare, welfareProbationDuration])
  const welfareSuspensionMembers = useMemo(() => {
    if (!isWelfare) return []
    return activePeopleMembers
      .map((member) => {
        if (String(member?.welfareStatus || "").toLowerCase() !== "suspended") return null
        return {
          id: String(member?.id || ""),
          name: member?.name || "Member",
          welfareMemberIdentifier: String(member?.welfareMemberIdentifier || "").trim(),
          suspendedAt: parseSafeDate(member?.welfareSuspendedAt),
          suspensionReason: String(member?.welfareSuspensionReason || "").trim(),
        }
      })
      .filter(Boolean)
  }, [activePeopleMembers, isWelfare])
  const visibleProbationMembers = useMemo(
    () => welfareProbationMembers.slice(0, probationMembersVisibleCount),
    [welfareProbationMembers, probationMembersVisibleCount]
  )
  const visibleSuspensionMembers = useMemo(
    () => welfareSuspensionMembers.slice(0, suspensionMembersVisibleCount),
    [welfareSuspensionMembers, suspensionMembersVisibleCount]
  )
  const canShowMoreProbationMembers =
    welfareProbationMembers.length > probationMembersVisibleCount
  const canShowMoreSuspensionMembers =
    welfareSuspensionMembers.length > suspensionMembersVisibleCount

  const adminCount = useMemo(
    () =>
      Array.isArray(group?.members)
        ? group.members.filter((member) => String(member?.role || "") === "admin").length
        : 0,
    [group?.members]
  )

  const pendingAdminRequests = useMemo(() => {
    const requests = Array.isArray(group?.adminRequests) ? group.adminRequests : []
    return requests.filter((request) => String(request?.status || "").toLowerCase() === "pending")
  }, [group?.adminRequests])
  const actionableAdminRequestCount = useMemo(
    () => pendingAdminRequests.filter((request) => !!request?.canApprove).length,
    [pendingAdminRequests]
  )
  const normalizedWelfareLossApprovalRequests = useMemo(() => {
    const rows = Array.isArray(welfareLossApprovalRequests)
      ? [...welfareLossApprovalRequests]
      : []
    const statusPriority = (request) =>
      String(request?.status || "").toLowerCase() === "pending" ? 0 : 1
    const createdAtTime = (request) => {
      const parsed = Date.parse(request?.createdAt || "")
      return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER
    }
    rows.sort((a, b) => {
      const priorityDiff = statusPriority(a) - statusPriority(b)
      if (priorityDiff !== 0) return priorityDiff
      const timeDiff = createdAtTime(a) - createdAtTime(b)
      if (timeDiff !== 0) return timeDiff
      return String(a?.id || "").localeCompare(String(b?.id || ""))
    })
    return rows
  }, [welfareLossApprovalRequests])
  const pendingWelfareLossApprovalCount = useMemo(
    () =>
      normalizedWelfareLossApprovalRequests.filter(
        (request) => String(request?.status || "").toLowerCase() === "pending"
      ).length,
    [normalizedWelfareLossApprovalRequests]
  )
  const visibleWelfareLossApprovalRequests = useMemo(
    () => normalizedWelfareLossApprovalRequests.slice(0, welfareLossApprovalVisibleCount),
    [normalizedWelfareLossApprovalRequests, welfareLossApprovalVisibleCount]
  )
  const actionableWelfareLossApprovalCount = useMemo(
    () => normalizedWelfareLossApprovalRequests.filter((request) => !!request?.canApprove).length,
    [normalizedWelfareLossApprovalRequests]
  )
  const canShowMoreWelfareLossApprovalRequests =
    normalizedWelfareLossApprovalRequests.length > welfareLossApprovalVisibleCount
  const welfareLossDecisionRequest = useMemo(() => {
    const requestId = String(welfareLossDecisionModal?.requestId || "")
    if (!requestId) return null
    return (
      normalizedWelfareLossApprovalRequests.find(
        (request) => String(request?.id || "") === requestId
      ) || null
    )
  }, [normalizedWelfareLossApprovalRequests, welfareLossDecisionModal?.requestId])
  const totalActionableApprovalCount =
    actionableAdminRequestCount + actionableWelfareLossApprovalCount + pendingJoinRequestsCount

  useEffect(() => {
    loadWelfareLossApprovalRequests()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id, isWelfare, isAdmin])

  useEffect(() => {
    setWelfareLossApprovalVisibleCount(3)
    setWelfareLossDecisionModal(null)
    setWelfareLossDecisionNote("")
    setWelfareLossDecisionError("")
    setIsProbationMembersOpen(false)
    setIsSuspensionMembersOpen(false)
    setProbationMembersVisibleCount(5)
    setSuspensionMembersVisibleCount(5)
    setPendingJoinRequestsCount(0)
    setPeopleSearch("")
    setPeopleVisibleCount(PEOPLE_VISIBLE_BATCH_SIZE)
    setJoinApprovalError("")
    setJoinApprovalSuccess("")
  }, [group?.id])

  useEffect(() => {
    setPeopleVisibleCount(PEOPLE_VISIBLE_BATCH_SIZE)
  }, [peopleSearch])

  useEffect(() => {
    const drafts = {}
    for (const member of peopleMembers) {
      if (member?.hasLeftGroup) continue
      if (String(member?.role || "") !== "admin") continue
      if (String(member?.adminTitle || "").toLowerCase() === "secretary") continue
      drafts[String(member.id)] = normalizeEditableAdminTitle(member?.adminTitle)
    }
    setAdminTitleDraftByMember(drafts)
  }, [peopleMembers])

  useEffect(() => {
    const draft = {}
    for (const member of nextCycleMembers) {
      draft[member.id] = String(member.shareCount || 1)
    }
    setNextCycleSharesDraft(draft)
  }, [nextCycleMembers])

  useEffect(() => {
    const nextCycle = group?.nextCycle
    if (!nextCycle) return
    setNextCycleFrequencyDraft(String(nextCycle?.invoiceFrequency || "Weekly"))
    setNextCycleAmountDraft(String(nextCycle?.contributionAmount ?? ""))
    setNextCycleMaxSharesDraft(String(nextCycle?.maxSharesPerMember || 1))
  }, [group?.nextCycle])

  useEffect(() => {
    setLateFeeDraft(String(Number(group?.lateFee ?? 0) || 0))
  }, [group?.id, group?.lateFee])

  const handleUpdateNextCycleAction = async (memberId, payload) => {
    setNextCycleError("")
    setNextCycleSuccess("")
    const result = await onUpdateNextCycleMember?.(memberId, payload)
    if (!result?.ok) {
      setNextCycleError(result?.message || "Failed to update next cycle member.")
      return
    }
    setNextCycleSuccess(result?.message || "Next cycle member updated.")
  }

  const handleToggleJoinApproval = async () => {
    if (!group?.id || !isJoinApprovalEditable || isUpdatingGroupJoinApproval) return
    setJoinApprovalError("")
    setJoinApprovalSuccess("")
    const result = await onUpdateGroupJoinApproval?.(!isJoinApprovalRequired)
    if (!result?.ok) {
      setJoinApprovalError(result?.message || "Failed to update joining approval.")
      return
    }
    setJoinApprovalSuccess(result?.message || "Joining approval updated.")
  }

  const handleSaveNextCycleShares = async (memberId) => {
    const value = Number(nextCycleSharesDraft?.[memberId] || 1)
    if (!Number.isFinite(value) || value < 1) {
      setNextCycleError("Shares must be at least 1.")
      return
    }
    await handleUpdateNextCycleAction(memberId, { desiredShares: value })
  }

    const openNextCycleActionModal = (action, member) => {
      if (!member?.id) return
      if (!isNextCycleEditable) {
      setNextCycleError("Next cycle has already started. Skip/rejoin changes are no longer allowed.")
        return
      }
      setNextCycleActionModal({
        action,
        memberId: member.id,
      memberName: member.name || "Member",
    })
  }

  const closeNextCycleActionModal = () => {
    if (isUpdatingNextCycleMember) return
    setNextCycleActionModal(null)
  }

  const handleConfirmNextCycleAction = async () => {
    if (!nextCycleActionModal?.memberId) return
    const payload =
      nextCycleActionModal.action === "leave"
        ? { leaveGroup: true }
        : nextCycleActionModal.action === "unskip"
          ? { skipCycle: false }
          : { skipCycle: true }
    await handleUpdateNextCycleAction(nextCycleActionModal.memberId, payload)
    setNextCycleActionModal(null)
  }

  const runAdminWorkflowAction = async (runner) => {
    setAdminWorkflowError("")
    setAdminWorkflowSuccess("")
    const result = await runner?.()
    if (!result?.ok) {
      setAdminWorkflowError(result?.message || "Action failed.")
      return
    }
    setAdminWorkflowSuccess(result?.message || "Action completed.")
  }

  const handlePromoteAdmin = async (memberId) => {
    await runAdminWorkflowAction(() => onPromoteGroupAdmin?.(memberId))
  }

  const handleSaveAdminTitle = async (memberId) => {
    const selectedTitle = String(adminTitleDraftByMember?.[memberId] || "none")
    await runAdminWorkflowAction(() => onUpdateAdminTitle?.(memberId, selectedTitle))
  }

  const handleRequestLeaveAdmin = async () => {
    await runAdminWorkflowAction(() => onRequestAdminLeave?.())
  }

  const handleRequestRemoveAdmin = async (memberId) => {
    await runAdminWorkflowAction(() => onRequestAdminRemoval?.(memberId))
  }

  const handleRequestSecretaryTransferAction = async (memberId) => {
    await runAdminWorkflowAction(() => onRequestSecretaryTransfer?.(memberId))
  }

  const handleApproveAdminRequestAction = async (requestId) => {
    await runAdminWorkflowAction(() => onApproveAdminRequest?.(requestId))
  }

  const handleRejectAdminRequestAction = async (requestId) => {
    await runAdminWorkflowAction(() => onRejectAdminRequest?.(requestId))
  }

  const handleImpersonateMemberAction = async (memberId) => {
    if (isArchivedReadOnly) {
      setAdminWorkflowError(archivedReadOnlyMessage)
      return
    }
    await runAdminWorkflowAction(() => onImpersonateMember?.(memberId))
  }

  const handleReactivateArchivedGroupAction = () => {
    if (!group?.id || !onReactivateArchivedGroup) return
    setReactivateArchivedGroupModalOpen(true)
  }

  const handleConfirmReactivateArchivedGroupAction = async () => {
    if (!group?.id || !onReactivateArchivedGroup) {
      return { ok: false, message: "Group not found." }
    }
    return onReactivateArchivedGroup(group.id)
  }

  async function loadWelfareLossApprovalRequests() {
    if (!group?.id || !isWelfare || !isAdmin) {
      setWelfareLossApprovalRequests([])
      setWelfareLossApprovalError("")
      setWelfareLossApprovalLoading(false)
      return
    }
    setWelfareLossApprovalLoading(true)
    setWelfareLossApprovalError("")
    try {
      const res = await api.get(`/groups/${group.id}/welfare/loss-reports/approval-requests`, {
        params: { status: "all" },
      })
      const rows = Array.isArray(res?.data?.approvalRequests) ? res.data.approvalRequests : []
      setWelfareLossApprovalRequests(rows)
    } catch (err) {
      setWelfareLossApprovalRequests([])
      setWelfareLossApprovalError(
        getApiErrorMessage(err, "Failed to load welfare loss report approvals.")
      )
    } finally {
      setWelfareLossApprovalLoading(false)
    }
  }

  const handleApproveWelfareLossReportRequest = async (requestId) => {
    if (!requestId) return
    setAdminWorkflowError("")
    setAdminWorkflowSuccess("")
    setWelfareLossDecisionError("")
    setWelfareLossDecisionModal({ requestId })
    setWelfareLossDecisionNote("")
  }

  const closeWelfareLossDecisionModal = () => {
    if (isUpdatingWelfareLossApproval) return
    setWelfareLossDecisionModal(null)
    setWelfareLossDecisionNote("")
    setWelfareLossDecisionError("")
  }

  const handleSubmitWelfareLossDecision = async () => {
    if (!group?.id || !welfareLossDecisionRequest?.id) return
    const note = String(welfareLossDecisionNote || "").trim()
    if (!note) {
      setWelfareLossDecisionError("Note/Description is required.")
      return
    }
    setWelfareLossDecisionError("")
    setAdminWorkflowSuccess("")
    setIsUpdatingWelfareLossApproval(true)
    try {
      const endpoint = `/groups/${group.id}/welfare/loss-reports/approval-requests/${welfareLossDecisionRequest.id}/approve`
      const res = await api.post(endpoint, { note })
      const updatedGroup = res?.data?.group
      if (updatedGroup?.id) {
        onGroupUpdated?.(updatedGroup)
      }
      setAdminWorkflowSuccess(
        res?.data?.message || "Loss report verified successfully."
      )
      setWelfareLossDecisionModal(null)
      setWelfareLossDecisionNote("")
      setWelfareLossDecisionError("")
      await loadWelfareLossApprovalRequests()
    } catch (err) {
      setWelfareLossDecisionError(
        getApiErrorMessage(err, "Failed to verify welfare loss report request.")
      )
    } finally {
      setIsUpdatingWelfareLossApproval(false)
    }
  }

  const handleSaveNextCycleSettings = async () => {
    setNextCycleError("")
    setNextCycleSuccess("")

    const amount = Number(nextCycleAmountDraft || 0)
    const maxShares = Number(nextCycleMaxSharesDraft || 1)
    const lateFee = Number(lateFeeDraft || 0)
    const frequency = String(nextCycleFrequencyDraft || "").trim()

    if (!frequency) {
      setNextCycleError("Frequency is required.")
      return
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setNextCycleError("Amount must be greater than 0.")
      return
    }
    if (!Number.isFinite(maxShares) || maxShares < 1) {
      setNextCycleError("Max shares must be at least 1.")
      return
    }
    if (!Number.isFinite(lateFee) || lateFee < 0) {
      setNextCycleError("Late fee cannot be negative.")
      return
    }

    const result = await onUpdateNextCycleSettings?.({
      invoiceFrequency: frequency,
      contributionAmount: amount,
      maxSharesPerMember: maxShares,
      lateFee,
    })
    if (!result?.ok) {
      setNextCycleError(result?.message || "Failed to update next cycle settings.")
      return
    }
    setNextCycleSuccess(result?.message || "Next cycle settings updated.")
  }

  if (!group) {
    return (
      <div className="bg-white/90 rounded-2xl border border-dashed border-gray-300 p-8 text-center text-xs text-gray-500">
        Select a group from <span className="font-semibold text-gray-700">Start</span> or{" "}
        <span className="font-semibold text-gray-700">Archived</span> to view details.
      </div>
    )
  }

  if (requiresWelfareOnboarding && !isArchivedReadOnly) {
    return (
      <div className={`space-y-4 p-3 sm:p-4 rounded-3xl transition-colors duration-500 ${theme.detailsBg}`}>
        {!!createError && (
          <div className="border border-red-300 bg-red-50 text-red-700 text-xs p-2 rounded-xl">
            {createError}
          </div>
        )}
        <GroupWelfareOnboardingModal
          group={group}
          onGroupUpdated={onGroupUpdated}
        />
      </div>
    )
  }

  return (
    <div className={`space-y-4 p-3 sm:p-4 rounded-3xl transition-colors duration-500 ${theme.detailsBg}`}>
      {/* Tab Navigation */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-2 noscrollbar">
        {tabs.map((tab) => {
          const Icon = tab.icon
          const isActive = activeTab === tab.id
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-all whitespace-nowrap
                ${isActive
                  ? `${theme.activeTab} shadow-md`
                  : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                }`}
            >
              <Icon size={14} />
              {tab.label}
              {tab.id === "admin-requests" && totalActionableApprovalCount > 0 && (
                <span className="ml-1 inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-red-600 px-1.5 text-[10px] font-bold leading-none text-white">
                  {totalActionableApprovalCount}
                </span>
              )}
            </button>
          )
        })}
      </div>

      {!!createError && (
        <div className="border border-red-300 bg-red-50 text-red-700 text-xs p-2 rounded-xl">
          {createError}
        </div>
      )}

      {!isRecurringGroup && (
        <div className={`bg-white/95 rounded-xl border ${theme.border} p-3 shadow-sm`}>
          <div className="flex min-w-0 items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-gray-800">Joining Approval Required</p>
              <p className="text-[11px] text-gray-500">
                ON: New members must be approved before joining.
              </p>
              <p className="text-[11px] text-gray-500">
                OFF: Members can join immediately without approval.
              </p>
            </div>
            <button
              type="button"
              onClick={handleToggleJoinApproval}
              disabled={isArchivedReadOnly || !isJoinApprovalEditable || isUpdatingGroupJoinApproval}
              className={`relative inline-flex h-6 w-14 shrink-0 items-center overflow-hidden rounded-full border transition-all shadow-sm disabled:opacity-60 ${
                isJoinApprovalRequired
                  ? "bg-[#3b82f6] border-[#3b82f6]"
                  : "bg-gray-200 border-gray-300"
              }`}
            >
              <span
                className={`absolute text-[9px] font-black tracking-tight ${
                  isJoinApprovalRequired ? "left-2 text-white" : "right-2 text-gray-500"
                }`}
              >
                {isUpdatingGroupJoinApproval ? "..." : isJoinApprovalRequired ? "ON" : "OFF"}
              </span>
              <span
                className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-md ring-1 ring-black/5 transition-transform duration-300 ease-in-out ${
                  isJoinApprovalRequired ? "translate-x-8" : "translate-x-0"
                }`}
              />
            </button>
          </div>
          {!!joinApprovalError && (
            <p className="mt-2 text-[11px] text-red-600">{joinApprovalError}</p>
          )}
          {!!joinApprovalSuccess && (
            <p className="mt-2 text-[11px] text-emerald-600">{joinApprovalSuccess}</p>
          )}
        </div>
      )}

      {isArchivedReadOnly && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span>{archivedReadOnlyMessage}</span>
          <button
            type="button"
            onClick={handleReactivateArchivedGroupAction}
            disabled={isReactivatingArchivedGroup}
            className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            {isReactivatingArchivedGroup ? "Reactivating..." : "Reactivate"}
          </button>
        </div>
      )}

      <div className={isArchivedReadOnly ? "pointer-events-none select-none opacity-75" : ""}>

      {/* WELFARE CURRENT STATUS TAB */}
      {activeTab === "current" && isWelfare && (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <GroupSummaryCard group={group} />

          <GroupMemberFinancePanel
            group={group}
            onGroupUpdated={onGroupUpdated}
            onUpdateMaxShares={onUpdateChamaaMaxShares}
            readOnly={isArchivedReadOnly}
          />
        </div>
      )}

      {/* WELFARE SETTINGS TAB */}
      {activeTab === "settings" && isWelfare && (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <GroupWelfareSettingsPanel
            group={group}
            isAdmin={isAdmin}
            onSaveSettings={onUpdateWelfareSettings}
            onAddChild={onAddWelfareChild}
            isAddingChild={isAddingWelfareChild}
            isSaving={isUpdatingWelfareSettings}
            readOnly={isArchivedReadOnly}
          />
          <GroupWelfareLossReportPanel
            group={group}
            onGroupUpdated={onGroupUpdated}
            readOnly={isArchivedReadOnly}
          />
        </div>
      )}

      {/* CURRENT CYCLE TAB */}
      {activeTab === "current-cycle" && !isWelfare && (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          {isNonRecurring ? (
            <GroupNonRecurringPlaceholderPanel
              group={group}
              onContribute={onContributeNonRecurring}
              isSubmitting={isContributingNonRecurring}
              readOnly={isArchivedReadOnly}
            />
          ) : (
            <>
              <GroupSummaryCard group={group} />

              {isAdmin && (
                <>
                  {isChamaa && (
                    <GroupChamaaCyclePanel
                      group={group}
                      onStartCycle={onStartChamaaCycle}
                      onPauseGroup={onPauseChamaaLifecycle}
                      onResumeGroup={onResumeChamaaLifecycle}
                      onStopGroup={onStopChamaaLifecycle}
                      isStartingCycle={isStartingChamaaCycle}
                      isPausingGroup={isPausingChamaaLifecycle}
                      isResumingGroup={isResumingChamaaLifecycle}
                      isStoppingGroup={isStoppingChamaaLifecycle}
                      readOnly={isArchivedReadOnly}
                    />
                  )}
                </>
              )}

              <GroupMemberFinancePanel
                group={group}
                onGroupUpdated={onGroupUpdated}
                onUpdateMaxShares={onUpdateChamaaMaxShares}
                readOnly={isArchivedReadOnly}
              />
            </>
          )}
        </div>
      )}

      {/* CHAMAA PAYMENT HISTORY (MOVED UNDER CYCLES TAB) */}
      {activeTab === "current-cycle" && isChamaa && (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="bg-white/95 rounded-2xl border border-gray-200 shadow-md p-4">
            {oldCyclesLoading ? (
              <p className="text-xs text-gray-500">Loading old cycles...</p>
            ) : !!oldCyclesError ? (
              <p className="text-xs text-red-600">{oldCyclesError}</p>
            ) : oldCycles.length === 0 ? (
              <p className="text-xs text-gray-500">No old cycles found for your membership yet.</p>
            ) : (
              <div className="space-y-4">
                <div className={`rounded-xl border ${theme.border} ${theme.mutedBg} p-3`}>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-3">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">Historical Cycles & Payments</h4>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-[10px] text-gray-600">
                      <span>Total Cycles: <span className="font-semibold text-gray-800">{oldCycles.length}</span></span>
                      <span>Outstanding Amount: <span className="font-semibold text-red-600">{formatMoney(historicalOutstandingTotals.amount)}</span></span>
                    </div>
                  </div>
                  {!!historicalPaymentError && (
                    <div className="mb-2 rounded-lg border border-red-300 bg-red-50 px-2 py-1.5 text-xs text-red-700">
                      {historicalPaymentError}
                    </div>
                  )}
                  {!!historicalPaymentSuccess && (
                    <div className="mb-2 rounded-lg border border-green-300 bg-green-50 px-2 py-1.5 text-xs text-green-700">
                      {historicalPaymentSuccess}
                    </div>
                  )}

                  <div className="space-y-3">
                    {historicalCyclesWithSortedInvoices.map((cycle) => {
                      const outstandingInvoices = Array.isArray(cycle?.outstandingInvoices) ? cycle.outstandingInvoices : []
                      const isCompleted = outstandingInvoices.length === 0
                      const cycleTotalShares = Number(cycle?.totalShares || 0)
                      const cyclePerShareAmount = Number(cycle?.contributionAmount || 0)
                      const cycleLateFeeAmount = Number(cycle?.lateFeeAmount ?? group?.lateFee ?? 0)

                      return (
                        <div key={`${cycle.id}-cycle`} className={`rounded-xl border bg-white ${isCompleted ? 'border-emerald-200' : 'border-rose-200 shadow-sm'}`}>
                          <div className={`flex flex-col gap-2 border-b px-3 py-2 sm:flex-row sm:items-center sm:justify-between ${isCompleted ? 'bg-emerald-50/30 border-emerald-100' : 'bg-rose-50/30 border-rose-100'}`}>
                            <div>
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex min-w-0 items-center gap-2">
                                  <p className="text-sm font-semibold text-gray-900">Cycle #{cycle.sequenceNumber || "-"}</p>
                                  {isCompleted ? (
                                    <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                                      <Check size={12} strokeWidth={3} aria-label="Completed" />
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700">
                                      <AlertTriangle size={12} strokeWidth={2.75} aria-label="Outstanding" />
                                    </span>
                                  )}
                                </div>
                                {isChamaa && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setHistoricalCycleWinnersModal({
                                        cycleKey: String(
                                          cycle?.id || cycle?._id || cycle?.sequenceNumber || ""
                                        ),
                                        cycleSequence: Number(cycle?.sequenceNumber || 0) || null,
                                      })
                                    }
                                    className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-amber-400 bg-amber-100 px-2.5 py-1 text-[10px] font-bold text-amber-800 shadow-sm ring-1 ring-amber-200 transition hover:bg-amber-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 active:scale-[0.98]"
                                  >
                                    <Trophy size={11} />
                                    Winners
                                  </button>
                                )}
                              </div>
                              <p className="text-[10px] text-gray-500">
                                {cycle.startDate ? formatDateLong(cycle.startDate) : "-"} - {cycle.endDate ? formatDateLong(cycle.endDate) : "-"}
                              </p>
                              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                <div className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[8px] sm:text-[9px] font-medium border ${
                                  isCompleted 
                                    ? 'bg-emerald-50/30 text-emerald-600 border-emerald-100' 
                                    : 'bg-rose-50/30 text-rose-600 border-rose-100'
                                }`}>
                                  <Users size={10} className="opacity-70" />
                                  <span>{cycleTotalShares || 0} Shares</span>
                                </div>
                                <div className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[8px] sm:text-[9px] font-medium border ${
                                  isCompleted 
                                    ? 'bg-emerald-50/30 text-emerald-600 border-emerald-100' 
                                    : 'bg-rose-50/30 text-rose-600 border-rose-100'
                                }`}>
                                  <Wallet size={10} className="opacity-70" />
                                  <span>{formatMoney(cyclePerShareAmount)} / Share</span>
                                </div>
                                <div className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[8px] sm:text-[9px] font-medium border ${
                                  isCompleted 
                                    ? 'bg-emerald-50/30 text-emerald-600 border-emerald-100' 
                                    : 'bg-rose-50/30 text-rose-600 border-rose-100'
                                }`}>
                                  <AlertTriangle size={10} className="opacity-70" />
                                  <span>{formatMoney(cycleLateFeeAmount)} Late Fee</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center justify-start gap-2 sm:justify-end sm:gap-3 text-[10px] text-gray-600">
                              <span>Status: <span className="font-semibold text-gray-800 capitalize">{cycle.status || "-"}</span></span>
                              {isCompleted ? (
                                <span className="font-semibold text-emerald-600">Fully Settled</span>
                              ) : (
                                <>
                                  <span>Open: <span className="font-semibold text-gray-800">{cycle.outstandingInvoiceCount || 0}</span></span>
                                  <span>Total: <span className="font-semibold text-rose-600">{formatMoney(cycle.outstandingAmount || 0)}</span></span>
                                </>
                              )}
                            </div>
                          </div>

                          {!isCompleted && (
                            <div className="overflow-hidden rounded-b-xl">
                              <table className="w-full table-fixed text-left border-collapse text-[8px] sm:text-xs">
                                <thead className={`${theme.bg} ${theme.text} font-semibold border-b ${theme.border}`}>
                                  <tr className="text-[8px] sm:text-[10px] md:text-xs">
                                    <th className="w-[16%] px-1 py-2 sm:px-3 md:py-2">Date</th>
                                    <th className="w-[22%] px-1 py-2 sm:px-3 md:py-2">Member</th>
                                    <th className="w-[8%] px-1 py-2 sm:px-3 md:py-2">R</th>
                                    <th className="w-[16%] px-1 py-2 sm:px-3 md:py-2">Status</th>
                                    <th className="px-2 py-2 md:px-3 md:py-2 text-right hidden sm:table-cell">Paid</th>
                                    <th className="w-[26%] px-1 py-2 sm:px-3 md:py-2 text-right">Bal</th>
                                    <th className="w-[12%] px-1 py-2 sm:px-3 md:py-2 text-right">Act</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 text-[8px] sm:text-[10px] md:text-xs text-gray-600">
                                  {outstandingInvoices.map((invoice) => {
                                    const statusKey = String(invoice?.status || "").toLowerCase()
                                    const statusClass =
                                      OLD_CYCLE_INVOICE_STATUS_CLASS[statusKey] ||
                                      "bg-gray-100 text-gray-600 border-gray-200"
                                    const isMine = String(invoice?.memberId || "") === String(group?.myMemberId || "")
                                    const invoiceDate = invoice?.issuedAt || invoice?.createdAt || invoice?.dueDate || null
                                    const canPay =
                                      Number(invoice?.balanceAmount || 0) > 0 &&
                                      !["paid", "cancelled"].includes(statusKey)
                                    return (
                                      <tr key={invoice.id} className={isMine ? `${theme.bg}/40` : ""}>
                                        <td className="px-1 py-1.5 sm:px-3 md:py-2 whitespace-normal break-words">
                                          {invoiceDate ? formatDateLong(invoiceDate) : "-"}
                                        </td>
                                        <td className="px-1 py-1.5 sm:px-3 md:py-2 font-medium text-gray-900 whitespace-normal break-words">
                                          {invoice.memberName || "-"} {isMine ? <span className={`${theme.text}`}>(You)</span> : null}
                                        </td>
                                        <td className="px-1 py-1.5 sm:px-3 md:py-2">R{invoice?.roundNumber || "-"}</td>
                                        <td className="px-1 py-1.5 sm:px-3 md:py-2">
                                          <span className={`inline-flex items-center rounded border px-1 py-0.5 text-[8px] sm:text-[10px] font-semibold uppercase whitespace-normal break-words ${statusClass}`}>
                                            {statusKey || "-"}
                                          </span>
                                        </td>
                                        <td className="px-2 py-1.5 md:px-3 md:py-2 text-right hidden sm:table-cell whitespace-nowrap text-emerald-600">
                                          {formatMoney(invoice?.paidAmount || 0)}
                                        </td>
                                        <td className="px-1 py-1.5 sm:px-3 md:py-2 text-right whitespace-normal break-words font-semibold text-red-600">
                                          {formatMoney(invoice?.balanceAmount || 0)}
                                        </td>
                                        <td className="px-1 py-1.5 sm:px-3 md:py-2 text-right whitespace-nowrap">
                                          {canPay && (
                                            <button
                                              type="button"
                                              onClick={() => handleRequestHistoricalInvoicePayment(invoice)}
                                              disabled={String(isPayingHistoricalInvoiceId || "") === String(invoice?.id || "")}
                                              className={`rounded-md px-1 py-1 sm:px-2 text-[8px] sm:text-[10px] font-semibold disabled:opacity-60 ${theme.button}`}
                                            >
                                              {String(isPayingHistoricalInvoiceId || "") === String(invoice?.id || "")
                                                ? "..."
                                                : "Pay"}
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ROUND WINNERS TAB */}
      {activeTab === "round-winners" && isChamaa && isAdmin && (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-yellow-50 shadow-md p-4">
            <div className="flex items-center justify-between gap-2 mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Round Recipients</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Recipients are selected randomly per round from eligible members. This tab tracks every round outcome.
                </p>
              </div>
              <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-xl bg-amber-100 text-amber-700 border border-amber-200">
                <Trophy size={18} />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-1.5 sm:gap-3 mb-4">
              <div className="rounded-xl border border-gray-100 bg-white p-2 sm:p-4 shadow-sm flex flex-col items-center text-center overflow-hidden">
                <Repeat size={16} className="text-gray-400 mb-1 sm:mb-2" />
                <p className="text-[8px] sm:text-[11px] font-medium text-gray-500 uppercase tracking-tight sm:tracking-wider truncate w-full">Rounds</p>
                <p className="text-base sm:text-2xl font-bold text-gray-900 my-0.5 sm:my-1">{winnerStats.total}</p>
                <p className="text-[7px] sm:text-[10px] text-gray-400 truncate w-full">Visible Rounds</p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-2 sm:p-4 shadow-sm flex flex-col items-center text-center overflow-hidden">
                <Wallet size={16} className="text-emerald-500 mb-1 sm:mb-2" />
                <p className="text-[8px] sm:text-[11px] font-medium text-gray-500 uppercase tracking-tight sm:tracking-wider truncate w-full">Payouts</p>
                <p className="text-base sm:text-2xl font-bold text-emerald-600 my-0.5 sm:my-1">{winnerStats.drawnAndPaid}</p>
                <p className="text-[7px] sm:text-[10px] text-gray-400 truncate w-full">Drawn & Paid</p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-2 sm:p-4 shadow-sm flex flex-col items-center text-center overflow-hidden">
                <AlertTriangle size={16} className="text-rose-500 mb-1 sm:mb-2" />
                <p className="text-[8px] sm:text-[11px] font-medium text-gray-500 uppercase tracking-tight sm:tracking-wider truncate w-full">Issues</p>
                <p className="text-base sm:text-2xl font-bold text-rose-600 my-0.5 sm:my-1">{winnerStats.lateOrDefault}</p>
                <p className="text-[7px] sm:text-[10px] text-gray-400 truncate w-full">Late / Default</p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-2 sm:p-4 shadow-sm flex flex-col items-center text-center overflow-hidden">
                <Users size={16} className={`${theme.text} mb-1 sm:mb-2`} />
                <p className="text-[8px] sm:text-[11px] font-medium text-gray-500 uppercase tracking-tight sm:tracking-wider truncate w-full">Members</p>
                <p className={`text-base sm:text-2xl font-bold my-0.5 sm:my-1 ${theme.text}`}>{winnerStats.uniqueWinners}</p>
                <p className="text-[7px] sm:text-[10px] text-gray-400 truncate w-full">Unique Recipients</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-3">
              <div>
                <label className="block text-[10px] font-bold text-gray-600 uppercase mb-1">Cycle</label>
                <select
                  value={winnerCycleFilter}
                  onChange={(e) => setWinnerCycleFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-amber-400 outline-none"
                >
                  <option value="all">All Cycles</option>
                  {cycleFilterOptions.map((cycle) => (
                    <option key={cycle?.id || cycle?._id || cycle?.sequenceNumber} value={String(cycle?.sequenceNumber || "")}>
                      Cycle #{cycle?.sequenceNumber || "-"} ({cycle?.status || "unknown"})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-600 uppercase mb-1">Recipient Status</label>
                <select
                  value={winnerStatusFilter}
                  onChange={(e) => setWinnerStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-amber-400 outline-none"
                >
                  <option value="all">All</option>
                  <option value="drawn">Recipient Drawn</option>
                  <option value="paid_out">Paid Out</option>
                  <option value="pending_draw">Pending Draw</option>
                </select>
              </div>
              <div className="col-span-2 md:col-span-1">
                <label className="block text-[10px] font-bold text-gray-600 uppercase mb-1">Recipient Name</label>
                <input
                  type="text"
                  value={winnerSearch}
                  onChange={(e) => setWinnerSearch(e.target.value)}
                  placeholder="Search recipient..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-amber-400 outline-none"
                />
              </div>
            </div>

            {oldCyclesLoading ? (
              <p className="text-xs text-gray-500">Loading recipient history...</p>
            ) : !!oldCyclesError ? (
              <p className="text-xs text-red-600">{oldCyclesError}</p>
            ) : roundWinnerRows.length === 0 ? (
              <p className="text-xs text-gray-500">No rounds match your current filters.</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full text-left border-collapse">
                  <thead className={`${theme.bg} ${theme.text} font-semibold border-b ${theme.border}`}>
                    <tr className="text-[10px] md:text-xs">
                      <th className="w-[8%] px-1 py-2 md:px-2 md:py-2">Cycle</th>
                      <th className="w-[7%] px-1 py-2 md:px-2 md:py-2">Rnd</th>
                      <th className="w-[15%] px-2 py-2 md:px-3 md:py-2 whitespace-nowrap">Date</th>
                      <th className="w-[26%] px-2 py-2 md:px-3 md:py-2">Recipient</th>
                      <th className="w-[26%] px-2 py-2 md:px-3 md:py-2">Round Status</th>
                      <th className="w-[8%] px-2 py-2 md:px-3 md:py-2 hidden sm:table-cell">Payout Due</th>
                      <th className="w-[8%] px-2 py-2 md:px-3 md:py-2 hidden sm:table-cell">Paid At</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-[10px] md:text-xs text-gray-600">
                    {roundWinnerRows.map((row) => {
                      const statusMeta = getWinnerStatusMeta(row)
                      return (
                        <tr key={row.key} className="hover:bg-gray-50">
                          <td className="px-1 py-1.5 md:px-2 md:py-2 font-medium text-gray-900">
                            #{row.cycleSequence}
                          </td>
                          <td className="px-1 py-1.5 md:px-2 md:py-2">R{row.roundNumber || "-"}</td>
                          <td className="px-2 py-1.5 md:px-3 md:py-2 whitespace-normal">
                            {renderDateWithYearBreak(row.roundDate)}
                          </td>
                          <td className="px-2 py-1.5 md:px-3 md:py-2">
                            <span className={row.winnerId ? "font-medium text-gray-900" : "italic text-gray-500"}>
                              {row.winnerName}
                            </span>
                          </td>
                          <td className="px-2 py-1.5 md:px-3 md:py-2">
                            <span className={`inline-flex w-[14ch] items-center justify-center whitespace-normal px-1 py-0.5 rounded text-[10px] font-semibold border leading-tight text-center ${statusMeta.className}`}>
                              {statusMeta.label}
                            </span>
                            {!!statusMeta.detail && (
                              <p className="mt-1 text-[9px] text-gray-500 leading-tight">{statusMeta.detail}</p>
                            )}
                          </td>
                          <td className="px-2 py-1.5 md:px-3 md:py-2 hidden sm:table-cell whitespace-nowrap">
                            {row.payoutScheduledAt ? formatDateLong(row.payoutScheduledAt) : "-"}
                          </td>
                          <td className="px-2 py-1.5 md:px-3 md:py-2 hidden sm:table-cell whitespace-nowrap">
                            {row.payoutProcessedAt ? formatDateLong(row.payoutProcessedAt) : "-"}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* PEOPLE TAB */}
      {activeTab === "people" && (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className={`bg-white/95 rounded-2xl border shadow-md p-4 ${theme.border}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Group Members ({filteredPeopleMembers.length}
                  {peopleSearch.trim() ? ` of ${peopleMembers.length}` : ""})
                </h3>
                {isAdmin && (
                  <p className="text-[10px] text-gray-500 mt-1">
                    Admins: <span className="font-semibold text-gray-700">{adminCount}</span> / 4
                    {isSecretary && <span className={`ml-2 font-semibold ${theme.text}`}>You are Secretary</span>}
                  </p>
                )}
                {isAdmin && isWelfare && !isWelfareEnrollmentOpen && (
                  <p className="text-[10px] text-amber-700 mt-1">
                    Invite disabled: Welfare enrollment is currently closed.
                  </p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1.5 sm:flex-row sm:items-center sm:gap-2">
                {isAdmin && (
                  <button
                    type="button"
                    onClick={handleRequestLeaveAdmin}
                    disabled={isUpdatingAdminWorkflow}
                    className="inline-flex items-center whitespace-nowrap leading-none px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-white bg-rose-500 hover:bg-rose-600 transition-colors shadow-sm disabled:opacity-60"
                  >
                    Leave Admin Role
                  </button>
                )}
                {isAdmin && (!isWelfare || isWelfareEnrollmentOpen) && (
                  <button
                    type="button"
                    onClick={() => onOpenInviteModal?.()}
                    className={`inline-flex items-center whitespace-nowrap leading-none px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-white transition-colors shadow-sm ${theme.button}`}
                  >
                    + Invite People
                  </button>
                )}
              </div>
            </div>
            <div className="mb-3">
              <input
                type="text"
                value={peopleSearch}
                onChange={(e) => setPeopleSearch(e.target.value)}
                placeholder="Search members by name..."
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-800 outline-none transition focus:border-gray-300 focus:ring-2 focus:ring-gray-100"
              />
            </div>

            {isWelfare && (
              <div className="mb-3 space-y-2">
                <div className="rounded-xl border border-gray-200 bg-white">
                  <button
                    type="button"
                    onClick={() => setIsProbationMembersOpen((prev) => !prev)}
                    className="w-full flex items-center justify-between px-3 py-2 text-left"
                  >
                    <span className="text-xs font-semibold text-gray-900">
                      Members in probation ({welfareProbationMembers.length})
                    </span>
                    {isProbationMembersOpen ? (
                      <ChevronDown size={14} className="text-gray-500" />
                    ) : (
                      <ChevronRight size={14} className="text-gray-500" />
                    )}
                  </button>
                  {isProbationMembersOpen && (
                    <div className="border-t border-gray-100 px-3 py-2">
                      {visibleProbationMembers.length === 0 ? (
                        <p className="text-xs text-gray-500">No members in probation.</p>
                      ) : (
                        <div className="space-y-2">
                          {visibleProbationMembers.map((member) => (
                            <div
                              key={`probation-${member.id}`}
                              className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2"
                            >
                              <p className="text-xs font-semibold text-gray-900">
                                {member.name}
                                {!!member.welfareMemberIdentifier && (
                                  <span className="ml-1 text-gray-500">
                                    ({member.welfareMemberIdentifier})
                                  </span>
                                )}
                              </p>
                              <p className="text-[11px] text-gray-700 mt-0.5">
                                Start:{" "}
                                <span className="font-medium">
                                  {member.startAt ? formatDateLong(member.startAt) : "-"}
                                </span>
                                {" | "}End:{" "}
                                <span className="font-medium">
                                  {member.endAt ? formatDateLong(member.endAt) : "-"}
                                </span>
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                      {canShowMoreProbationMembers && (
                        <button
                          type="button"
                          onClick={() => setProbationMembersVisibleCount((prev) => prev + 5)}
                          className="mt-2 w-full rounded-lg border border-gray-200 bg-white py-1.5 text-[11px] font-semibold text-gray-600 hover:bg-gray-50"
                        >
                          Show more
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-gray-200 bg-white">
                  <button
                    type="button"
                    onClick={() => setIsSuspensionMembersOpen((prev) => !prev)}
                    className="w-full flex items-center justify-between px-3 py-2 text-left"
                  >
                    <span className="text-xs font-semibold text-gray-900">
                      Members in suspension ({welfareSuspensionMembers.length})
                    </span>
                    {isSuspensionMembersOpen ? (
                      <ChevronDown size={14} className="text-gray-500" />
                    ) : (
                      <ChevronRight size={14} className="text-gray-500" />
                    )}
                  </button>
                  {isSuspensionMembersOpen && (
                    <div className="border-t border-gray-100 px-3 py-2">
                      {visibleSuspensionMembers.length === 0 ? (
                        <p className="text-xs text-gray-500">No suspended members.</p>
                      ) : (
                        <div className="space-y-2">
                          {visibleSuspensionMembers.map((member) => (
                            <div
                              key={`suspension-${member.id}`}
                              className="rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-2"
                            >
                              <p className="text-xs font-semibold text-gray-900">
                                {member.name}
                                {!!member.welfareMemberIdentifier && (
                                  <span className="ml-1 text-gray-500">
                                    ({member.welfareMemberIdentifier})
                                  </span>
                                )}
                              </p>
                              <p className="text-[11px] text-gray-700 mt-0.5">
                                Suspended:{" "}
                                <span className="font-medium">
                                  {member.suspendedAt ? formatDateLong(member.suspendedAt) : "-"}
                                </span>
                                {" | "}Reason:{" "}
                                <span className="font-medium">
                                  {member.suspensionReason || "-"}
                                </span>
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                      {canShowMoreSuspensionMembers && (
                        <button
                          type="button"
                          onClick={() => setSuspensionMembersVisibleCount((prev) => prev + 5)}
                          className="mt-2 w-full rounded-lg border border-gray-200 bg-white py-1.5 text-[11px] font-semibold text-gray-600 hover:bg-gray-50"
                        >
                          Show more
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Simple Member List */}
            <div className="space-y-2">
              {visiblePeopleMembers.map((member) => {
                const welfareProbationDisplay =
                  isWelfare && !member?.hasLeftGroup
                    ? resolveWelfareMemberProbationDisplay({
                        member,
                        group,
                        probationDuration: welfareProbationDuration,
                      })
                    : null
                const isMemberAdmin =
                  !member?.hasLeftGroup && String(member?.role || "").toLowerCase() === "admin"
                const adminTitleLabel = resolveAdminTitleLabel(member?.adminTitle)
                const adminTitleBadgeClass = getAdminTitleBadgeClass(member?.adminTitle)

                return (
                  <div
                    key={member.hasLeftGroup ? `history-${member.id}-${member.leftAt || ""}` : member.id}
                    className={`flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between p-3 rounded-xl border ${theme.mutedBg} ${theme.border}/50`}
                  >
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${theme.bg} ${theme.text}`}>
                        {member.name?.charAt(0) || "?"}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          {member.name}
                        </p>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                          {isMemberAdmin && (
                            <span
                              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide ${adminTitleBadgeClass}`}
                            >
                              {adminTitleLabel}
                            </span>
                          )}
                          {member.hasLeftGroup && (
                            <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-700">
                              Left group
                            </span>
                          )}
                        </div>
                        {member.hasLeftGroup ? (
                          <p className="text-[10px] text-gray-500 capitalize">
                            {member.role} | left on {member.leftAt ? formatDateLong(member.leftAt) : "-"}
                          </p>
                        ) : (
                          <>
                            <p className="text-[10px] text-gray-500 capitalize">
                              {member.role} | {member.membershipStatus} |{" "}
                              {isProjectSavings
                                ? `target ${formatMoney(member?.projectSavingsTargetAmount || 0)}`
                                : `shares ${member.desiredShares || 1}`}
                            </p>
                            {isWelfare && (
                              <p className="text-[10px] text-gray-500 mt-0.5">
                                Welfare status:{" "}
                                <span className="font-semibold text-gray-700 capitalize">
                                  {String(member?.welfareStatus || "active")}
                                </span>
                                {welfareProbationDisplay ? (
                                  <>
                                    {" | "}
                                    <span className="font-semibold text-blue-700">
                                      {welfareProbationDisplay.scenarioLabel}
                                    </span>
                                    {" | "}Start:{" "}
                                    <span className="font-semibold text-gray-700">
                                      {welfareProbationDisplay.startAt
                                        ? formatDateLong(welfareProbationDisplay.startAt)
                                        : "-"}
                                    </span>
                                    {" | "}End:{" "}
                                    <span className="font-semibold text-gray-700">
                                      {welfareProbationDisplay.endAt
                                        ? formatDateLong(welfareProbationDisplay.endAt)
                                        : "-"}
                                    </span>
                                  </>
                                ) : null}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  {!member.hasLeftGroup && (
                    <div className="w-full sm:w-auto flex flex-col items-stretch sm:items-end gap-1.5 shrink-0">
                      {isAdmin &&
                        !isGroupScopedImpersonation &&
                        String(member?.id || "") !== String(group?.myMemberId || "") && (
                          <button
                            type="button"
                            onClick={() => handleImpersonateMemberAction(member.id)}
                            disabled={isUpdatingAdminWorkflow}
                            className={`w-full sm:w-auto px-2 py-1 rounded text-[10px] font-medium border disabled:opacity-50 whitespace-nowrap transition-all ${theme.bg} ${theme.text} ${theme.border} hover:opacity-80`}
                          >
                            View Account
                          </button>
                        )}
                      {isSecretary &&
                        String(member?.role || "") !== "admin" &&
                        adminCount < 4 && (
                          <button
                            type="button"
                            onClick={() => handlePromoteAdmin(member.id)}
                            disabled={isUpdatingAdminWorkflow}
                            className={`w-full sm:w-auto px-2 py-1 rounded text-[10px] font-medium border disabled:opacity-50 whitespace-nowrap transition-all ${theme.bg} ${theme.text} ${theme.border} hover:opacity-80`}
                          >
                            Make Admin
                          </button>
                        )}
                      {isAdmin &&
                        String(member?.role || "") === "admin" &&
                        String(member?.id || "") !== String(group?.myMemberId || "") && (
                          <>
                            {isSecretary &&
                              String(member?.adminTitle || "").toLowerCase() !== "secretary" && (
                                <div className={`flex w-full sm:w-auto flex-col sm:flex-row items-stretch sm:items-center gap-1 rounded border px-1.5 py-1 ${theme.bg} ${theme.border}`}>
                                  <select
                                    value={String(adminTitleDraftByMember[String(member.id)] || "none")}
                                    onChange={(e) =>
                                      setAdminTitleDraftByMember((prev) => ({
                                        ...prev,
                                        [String(member.id)]: String(e.target.value || "none"),
                                      }))
                                    }
                                    className={`w-full sm:w-auto px-1.5 py-1 rounded border bg-white text-[10px] font-medium outline-none ${theme.text} ${theme.border}`}
                                  >
                                    <option value="none">No title</option>
                                    {ADMIN_TITLE_OPTIONS.map((option) => (
                                      <option key={option.value} value={option.value}>
                                        {option.label}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    onClick={() => handleSaveAdminTitle(member.id)}
                                    disabled={isUpdatingAdminWorkflow}
                                    className={`w-full sm:w-auto px-2 py-1 rounded text-[10px] font-medium bg-white border disabled:opacity-50 whitespace-nowrap transition-all ${theme.text} ${theme.border} hover:opacity-80`}
                                  >
                                    Save Title
                                  </button>
                                </div>
                              )}
                            {isSecretary && (
                              <button
                                type="button"
                                onClick={() => handleRequestSecretaryTransferAction(member.id)}
                                disabled={isUpdatingAdminWorkflow}
                                className={`w-full sm:w-auto px-2 py-1 rounded text-[10px] font-medium border disabled:opacity-50 whitespace-nowrap transition-all ${theme.bg} ${theme.text} ${theme.border} hover:opacity-80`}
                              >
                                Assign as Secretary
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRequestRemoveAdmin(member.id)}
                              disabled={isUpdatingAdminWorkflow || adminCount <= 2}
                              className="w-full sm:w-auto px-2 py-1 rounded text-[10px] font-medium text-rose-700 bg-rose-50 hover:bg-rose-100 border border-rose-200 disabled:opacity-50 whitespace-nowrap"
                            >
                              Request Removal of This Admin
                            </button>
                          </>
                        )}
                    </div>
                  )}
                </div>
                )
              })}
              {filteredPeopleMembers.length === 0 && (
                <p className="text-xs text-gray-500 text-center py-4">
                  {peopleSearch.trim()
                    ? `No members found for "${peopleSearch.trim()}".`
                    : "No members found."}
                </p>
              )}
              {canShowMorePeopleMembers && (
                <button
                  type="button"
                  onClick={() => setPeopleVisibleCount((prev) => prev + PEOPLE_VISIBLE_BATCH_SIZE)}
                  className="w-full rounded-lg border border-gray-200 bg-white py-1.5 text-[11px] font-semibold text-gray-700 hover:bg-gray-50"
                >
                  View more
                </button>
              )}
            </div>

            {!!adminWorkflowError && (
              <div className="mt-3 border border-red-300 bg-red-50 text-red-700 text-xs p-2 rounded-lg">
                {adminWorkflowError}
              </div>
            )}
            {!!adminWorkflowSuccess && (
              <div className="mt-3 border border-green-300 bg-green-50 text-green-700 text-xs p-2 rounded-lg">
                {adminWorkflowSuccess}
              </div>
            )}
          </div>

          {isAdmin && (
            <GroupPendingInvitesPanel
              invites={pendingInvites}
              isLoading={pendingInvitesLoading}
              error={pendingInvitesError}
            />
          )}
        </div>
      )}

      {/* ADMIN REQUESTS TAB */}
      {activeTab === "admin-requests" && isAdmin && (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="bg-white/95 rounded-2xl border border-gray-200 shadow-md p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-gray-900">Admin Approval Notifications</h3>
              {isWelfare && (
                <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700">
                  Pending: {pendingWelfareLossApprovalCount}
                </span>
              )}
            </div>

            <GroupJoinRequestsPanel
              groupId={group?.id}
              isAdmin={isAdmin}
              disabled={isUpdatingAdminWorkflow || isUpdatingWelfareLossApproval}
              readOnly={isArchivedReadOnly}
              onPendingCountChange={setPendingJoinRequestsCount}
            />

            {pendingAdminRequests.length > 0 && (
              <div className="space-y-2 mt-4 pt-4 border-t border-gray-100">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  Admin workflow
                </p>
                {pendingAdminRequests.map((request) => {
                  const isLeave = String(request?.type || "") === "leave_admin"
                  const isTransfer = String(request?.type || "") === "transfer_secretary"
                  const requestTitle = isTransfer
                    ? "Secretary Transfer Acceptance"
                    : isLeave
                      ? "Admin Leave Approval"
                      : "Admin Removal Approval"
                  return (
                    <div
                      key={request.id}
                      className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2"
                    >
                      <p className="text-xs font-semibold text-gray-900">{requestTitle}</p>
                      <p className="text-[11px] text-gray-600 mt-0.5">
                        Target: <span className="font-medium">{request?.targetUserName || "-"}</span>
                        {" | "}Requested by: <span className="font-medium">{request?.requestedByName || "-"}</span>
                      </p>
                      <p className="text-[11px] text-gray-500 mt-0.5">
                        Approvals: {Number(request?.approvalCount || 0)} / {Number(request?.requiredApprovals || 1)}
                      </p>
                      {request?.canApprove && (
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleApproveAdminRequestAction(request.id)}
                            disabled={isUpdatingAdminWorkflow || isUpdatingWelfareLossApproval}
                            className="px-2 py-1 rounded text-[10px] font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 disabled:opacity-50"
                          >
                            {isTransfer ? "Accept" : "Approve"}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRejectAdminRequestAction(request.id)}
                            disabled={isUpdatingAdminWorkflow || isUpdatingWelfareLossApproval}
                            className="px-2 py-1 rounded text-[10px] font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}

            {isWelfare && (
              <div className={pendingAdminRequests.length > 0 ? "mt-4 pt-4 border-t border-gray-100" : ""}>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                    Admin Loss Papers Verification
                  </p>
                  <button
                    type="button"
                    onClick={loadWelfareLossApprovalRequests}
                    disabled={welfareLossApprovalLoading || isUpdatingWelfareLossApproval}
                    className="px-2 py-1 rounded border border-gray-200 text-[10px] font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Refresh
                  </button>
                </div>

                {welfareLossApprovalLoading ? (
                  <p className="text-xs text-gray-500">Loading welfare loss report approvals...</p>
                ) : welfareLossApprovalError ? (
                  <p className="text-xs text-red-600">{welfareLossApprovalError}</p>
                ) : normalizedWelfareLossApprovalRequests.length === 0 ? (
                  <p className="text-xs text-gray-500">No welfare loss report records yet.</p>
                ) : (
                  <div className="space-y-2">
                    {visibleWelfareLossApprovalRequests.map((request) => {
                      const statusMeta = getWelfareLossApprovalStatusMeta(request?.status)
                      const status = String(request?.status || "").toLowerCase()
                      const isPending = status === "pending"
                      const isApproved = status === "approved"
                      const isRejected = status === "rejected"
                      const decisionByName = isApproved
                        ? request?.approvedByName || "-"
                        : isRejected
                          ? request?.rejectedByName || "-"
                          : ""
                      const decisionAt = isApproved
                        ? request?.approvedAt
                        : isRejected
                          ? request?.rejectedAt
                          : null
                      const decisionNote = isApproved
                        ? String(request?.approvalNote || "").trim()
                        : isRejected
                          ? String(request?.rejectionReason || "").trim()
                          : ""

                      return (
                        <div
                          key={request?.id}
                          className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-xs font-semibold text-gray-900">
                              {request?.subjectLabel || "Loss report"}{" "}
                              <span className="text-gray-500">| {request?.relationLabel || "Member"}</span>
                            </p>
                            <span
                              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${statusMeta.className}`}
                            >
                              {statusMeta.label}
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-600 mt-0.5">
                            Claimant: <span className="font-medium">{request?.claimantName || "-"}</span>
                            {!!request?.claimantWelfareMemberIdentifier && (
                              <span className="ml-1 text-gray-500">
                                ({request.claimantWelfareMemberIdentifier})
                              </span>
                            )}
                          </p>
                          <p className="text-[11px] text-gray-600 mt-0.5">
                            Amount:{" "}
                            <span className="font-semibold">
                              {formatMoney(Number(request?.benefitAmountSnapshot || 0))}
                            </span>
                            {" | "}Projected Ref:{" "}
                            <span className="font-semibold">
                              {request?.projectedReferenceCode || "-"}
                            </span>
                          </p>
                          <p className="text-[11px] text-gray-500 mt-0.5">
                            Submitted:{" "}
                            <span className="font-medium">{formatDateLong(request?.createdAt)}</span>
                          </p>

                          {!isPending && (
                            <div className="mt-2 rounded-lg border border-slate-200 bg-slate-100/80 px-2 py-1.5">
                              {decisionAt && (
                                <p className="text-[11px] text-slate-700">
                                  Updated by: <span className="font-semibold">{decisionByName}</span>
                                  {" | "}On: <span className="font-semibold">{formatDateLong(decisionAt)}</span>
                                </p>
                              )}
                              {!!decisionNote && (
                                <p className="text-[11px] text-slate-700 mt-0.5">
                                  Note/Description:{" "}
                                  <span className="font-medium text-slate-900">{decisionNote}</span>
                                </p>
                              )}
                            </div>
                          )}

                          {isPending && (
                            <div className="mt-2 flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleApproveWelfareLossReportRequest(request?.id)}
                                disabled={
                                  !request?.canApprove ||
                                  isUpdatingAdminWorkflow ||
                                  isUpdatingWelfareLossApproval
                                }
                                className="px-2 py-1 rounded text-[10px] font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 disabled:opacity-50"
                              >
                                Next
                              </button>
                            </div>
                          )}
                        </div>
                      )
                    })}
                    {canShowMoreWelfareLossApprovalRequests && (
                      <button
                        type="button"
                        onClick={() =>
                          setWelfareLossApprovalVisibleCount((prev) => prev + 3)
                        }
                        className="w-full rounded-lg border border-gray-200 bg-white py-1.5 text-[11px] font-semibold text-gray-600 hover:bg-gray-50"
                      >
                        Show more
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {pendingJoinRequestsCount === 0 &&
              pendingAdminRequests.length === 0 &&
              (!isWelfare ||
                (!welfareLossApprovalLoading &&
                  !welfareLossApprovalError &&
                  normalizedWelfareLossApprovalRequests.length === 0)) && (
                <p className="text-xs text-gray-500 mt-3">No pending approval notifications.</p>
              )}
          </div>
        </div>
      )}

      {/* NEXT CYCLE TAB */}
      {activeTab === "next-cycle" && (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className={`bg-white/95 rounded-2xl border ${theme.border} shadow-md p-4`}>
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1">Next Cycle</h3>
                <p className="text-xs text-gray-500">
                  Members planned for Cycle #{group?.nextCycle?.sequenceNumber || "-"}
                </p>
              </div>
              {!!group?.nextCycle && (
                <div className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 ${theme.bg} ${theme.border}`}>
                  <span className={`text-[10px] font-bold uppercase tracking-wide ${theme.text}`}>Due Date</span>
                  <span className="text-xs font-semibold text-gray-800">
                    {group?.nextCycle?.startDate ? formatDateLong(group.nextCycle.startDate) : "TBC"}
                  </span>
                </div>
              )}
            </div>

            {isAdmin && !!group?.nextCycle && (
              <div className={`mb-4 rounded-xl border p-3 ${theme.border} ${theme.mutedBg}`}>
                <h4 className={`text-xs font-bold uppercase tracking-wide mb-2 ${theme.text}`}>
                  Next Cycle Settings
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  <div>
                    <label className={`block text-[10px] font-bold uppercase mb-1 ${theme.text}`}>
                      Frequency
                    </label>
                    <select
                      value={nextCycleFrequencyDraft}
                      onChange={(e) => setNextCycleFrequencyDraft(e.target.value)}
                      className={`w-full px-2.5 py-2 border rounded-lg text-xs bg-white focus:ring-2 outline-none ${theme.border} ${theme.ring}`}
                    >
                      {(() => {
                        const opts = new Set(NEXT_CYCLE_FREQUENCY_OPTIONS)
                        const current = String(nextCycleFrequencyDraft || "").trim()
                        if (current) opts.add(current)
                        return Array.from(opts).map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))
                      })()}
                    </select>
                  </div>
                  <div>
                    <label className={`block text-[10px] font-bold uppercase mb-1 ${theme.text}`}>
                      Amount
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={nextCycleAmountDraft}
                      onChange={(e) => setNextCycleAmountDraft(e.target.value)}
                      className={`w-full px-2.5 py-2 border rounded-lg text-xs focus:ring-2 outline-none bg-white ${theme.border} ${theme.ring}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-[10px] font-bold uppercase mb-1 ${theme.text}`}>
                      Max Shares
                    </label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={nextCycleMaxSharesDraft}
                      onChange={(e) => setNextCycleMaxSharesDraft(e.target.value)}
                      className={`w-full px-2.5 py-2 border rounded-lg text-xs focus:ring-2 outline-none bg-white ${theme.border} ${theme.ring}`}
                    />
                  </div>
                  <div>
                    <label className={`block text-[10px] font-bold uppercase mb-1 ${theme.text}`}>
                      Late Fee
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={lateFeeDraft}
                      onChange={(e) => setLateFeeDraft(e.target.value)}
                      className={`w-full px-2.5 py-2 border rounded-lg text-xs focus:ring-2 outline-none bg-white ${theme.border} ${theme.ring}`}
                    />
                  </div>
                </div>
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={handleSaveNextCycleSettings}
                    disabled={isUpdatingNextCycleSettings}
                    className={`px-3 py-1.5 rounded-lg text-[10px] font-bold text-white uppercase tracking-wide disabled:opacity-60 shadow-sm transition-all ${theme.button}`}
                  >
                    {isUpdatingNextCycleSettings ? "Saving..." : "Save Settings"}
                  </button>
                </div>
              </div>
            )}

            {!group?.nextCycle ? (
              <p className="text-xs text-gray-500">Next cycle is being prepared.</p>
            ) : nextCycleMembers.length === 0 ? (
              <p className="text-xs text-gray-500">No members are currently planned for the next cycle.</p>
            ) : (
              <div className="space-y-2">
	                {!isNextCycleEditable && (
	                  <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5">
	                    Next cycle is no longer scheduled. Skip/rejoin is locked until a new scheduled cycle is available.
	                  </p>
	                )}
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <table className="w-full table-fixed text-left border-collapse text-[10px] sm:text-xs">
                  <thead className="bg-gray-50 text-gray-700 font-semibold border-b border-gray-200">
                    <tr>
                      <th className="w-[35%] px-2 py-2">Member</th>
                      <th className="w-[20%] px-2 py-2 hidden sm:table-cell">Role</th>
                      <th className="w-[20%] px-2 py-2">Shares</th>
                      <th className="w-[25%] px-2 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-gray-600 bg-white">
                    {nextCycleMembers.map((member) => {
                      const canEdit = isAdmin || member.isMe
                      return (
                        <tr key={member.id} className={`hover:bg-gray-50 ${member.isMe ? `${theme.bg}/30` : ""}`}>
                          <td className="px-2 py-2 truncate">
                            <span className={`font-medium ${member.isMe ? theme.text : "text-gray-900"}`}>
                              {member.name}
                              {member.isMe && <span className={`ml-1 ${theme.text}`}>(You)</span>}
                            </span>
                            <div className="text-[9px] text-gray-400 sm:hidden capitalize">{member.role}</div>
                            <div className="mt-0.5">
                              <span
                                className={`inline-flex rounded px-1.5 py-0.5 text-[9px] font-semibold ${
                                  member.isLeavingAfterCurrentCycle
                                    ? "bg-red-50 text-red-700 border border-red-200"
                                    : member.isExplicitlySkipped
                                      ? "bg-amber-50 text-amber-700 border border-amber-200"
                                      : member.isIncludedInNextCycle
                                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                        : "bg-slate-50 text-slate-600 border border-slate-200"
                                }`}
                              >
                                {member.nextCycleStateLabel}
                              </span>
                            </div>
                          </td>
                          <td className="px-2 py-2 hidden sm:table-cell capitalize text-gray-700 truncate">
                            {member.role}
                          </td>
                          <td className="px-2 py-2">
                            {canEdit ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  min="1"
                                  max={Number(group?.nextCycle?.maxSharesPerMember || group?.maxSharesPerMember || 1)}
                                  step="1"
                                  value={nextCycleSharesDraft?.[member.id] ?? String(member.shareCount || 1)}
                                  onChange={(e) =>
                                    setNextCycleSharesDraft((prev) => ({
                                      ...prev,
                                      [member.id]: e.target.value,
                                    }))
                                  }
                                  className={`w-8 sm:w-10 px-1 py-0.5 border rounded text-[9px] outline-none focus:ring-1 ${theme.border} ${theme.ring}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => handleSaveNextCycleShares(member.id)}
                                  disabled={isUpdatingNextCycleMember}
                                  className={`px-1.5 py-0.5 rounded text-[9px] font-semibold text-white disabled:opacity-50 transition-all ${theme.button}`}
                                >
                                  OK
                                </button>
                              </div>
                            ) : (
                              <span className="font-medium text-gray-900">{member.shareCount}</span>
                            )}
                          </td>
                          <td className="px-2 py-2 text-right">
                            {canEdit ? (
                              <div className="flex flex-col sm:flex-row justify-end gap-1">
                                <button
                                  type="button"
                                  onClick={() => openNextCycleActionModal(member.skipActionMode, member)}
                                  disabled={isUpdatingNextCycleMember || !member.skipActionEnabled || !isNextCycleEditable}
                                  className="px-1.5 py-0.5 rounded text-[8px] font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 disabled:opacity-50 whitespace-nowrap"
	                                >
	                                  {member.skipActionMode === "unskip" ? "Rejoin" : "Skip"}
	                                </button>
                                <button
                                  type="button"
                                  onClick={() => openNextCycleActionModal("leave", member)}
                                  disabled={
                                    isUpdatingNextCycleMember ||
                                    String(member?.role || "") === "admin" ||
                                    member.isLeavingAfterCurrentCycle ||
                                    !isNextCycleEditable
                                  }
                                  className="px-1.5 py-0.5 rounded text-[8px] font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 disabled:opacity-50 whitespace-nowrap"
                                >
                                  {member.isLeavingAfterCurrentCycle ? "Leaving" : "Leave"}
                                </button>
                              </div>
                            ) : (
                              <span className="text-[10px] text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              </div>
            )}

            {!!nextCycleError && (
              <div className="border border-red-300 bg-red-50 text-red-700 text-xs p-2 rounded-lg mt-3">
                {nextCycleError}
              </div>
            )}
            {!!nextCycleSuccess && (
              <div className="border border-green-300 bg-green-50 text-green-700 text-xs p-2 rounded-lg mt-3">
                {nextCycleSuccess}
              </div>
            )}
          </div>
        </div>
      )}

      {/* SIMULATOR TAB */}
      {activeTab === "simulator" && isChamaa && (
        <GroupChamaaSimulatorPanel
          group={group}
          isAdmin={isAdmin}
          onUpdateSimulatorConfig={onUpdateGroupSimulator}
          onAdvanceSimulator={onAdvanceGroupSimulator}
          isUpdatingSimulator={isUpdatingGroupSimulator}
          isAdvancingSimulator={isAdvancingGroupSimulator}
          readOnly={isArchivedReadOnly}
        />
      )}

      </div>

      <GroupArchiveConfirmModal
        open={reactivateArchivedGroupModalOpen}
        mode="reactivate"
        groupName={group?.name || ""}
        isSubmitting={isReactivatingArchivedGroup}
        onClose={() => setReactivateArchivedGroupModalOpen(false)}
        onConfirm={handleConfirmReactivateArchivedGroupAction}
      />

      {!!historicalCycleWinnersModal && (
        <div
          className="fixed inset-0 z-[130] flex items-center justify-center bg-black/45 p-3 sm:p-4"
          onClick={closeHistoricalCycleWinnersModal}
        >
          <div
            className="w-full max-w-3xl rounded-2xl border border-amber-200 bg-white shadow-xl max-h-[92vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2 border-b border-amber-200 bg-gradient-to-r from-amber-50 via-white to-yellow-50 px-3 py-3 sm:px-4">
              <div>
                <h4 className="text-sm font-semibold text-gray-900">
                  Cycle #{selectedHistoricalWinnersCycle?.sequenceNumber || historicalCycleWinnersModal?.cycleSequence || "-"} Winners
                </h4>
                <p className="text-[11px] text-gray-600 mt-1">
                  Round recipients and payout status for this cycle.
                </p>
              </div>
              <button
                type="button"
                onClick={closeHistoricalCycleWinnersModal}
                className="rounded-lg border border-gray-300 px-2.5 py-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>

            <div className="max-h-[74vh] overflow-y-auto p-3 sm:p-4">
              <div className="mb-3">
                <label className="block text-[10px] font-bold text-gray-600 uppercase mb-1">Recipient Name</label>
                <input
                  type="text"
                  value={historicalWinnersSearch}
                  onChange={(e) => setHistoricalWinnersSearch(e.target.value)}
                  placeholder="Search recipient..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-amber-400 outline-none"
                />
              </div>

              {selectedHistoricalCycleWinnerRows.length === 0 ? (
                <p className="text-xs text-gray-500">No round recipient data found for this cycle yet.</p>
              ) : filteredHistoricalCycleWinnerRows.length === 0 ? (
                <p className="text-xs text-gray-500">No rounds match your search.</p>
              ) : (
                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <table className="w-full text-left border-collapse">
                    <thead className={`${theme.bg} ${theme.text} font-semibold border-b ${theme.border}`}>
                      <tr className="text-[10px] md:text-xs">
                        <th className="px-2 py-2 md:px-3 md:py-2">Rnd</th>
                        <th className="px-2 py-2 md:px-3 md:py-2 whitespace-nowrap">Date</th>
                        <th className="px-2 py-2 md:px-3 md:py-2">Recipient</th>
                        <th className="px-2 py-2 md:px-3 md:py-2">Round Status</th>
                        <th className="px-2 py-2 md:px-3 md:py-2 hidden sm:table-cell">Payout Due</th>
                        <th className="px-2 py-2 md:px-3 md:py-2 hidden sm:table-cell">Paid At</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-[10px] md:text-xs text-gray-600">
                      {filteredHistoricalCycleWinnerRows.map((row) => {
                        const statusMeta = getWinnerStatusMeta(row)
                        return (
                          <tr key={`modal-${row.key}`} className="hover:bg-gray-50">
                            <td className="px-2 py-1.5 md:px-3 md:py-2">R{row.roundNumber || "-"}</td>
                            <td className="px-2 py-1.5 md:px-3 md:py-2 whitespace-normal">
                              {renderDateWithYearBreak(row.roundDate)}
                            </td>
                            <td className="px-2 py-1.5 md:px-3 md:py-2">
                              <span className={row.winnerId ? "font-medium text-gray-900" : "italic text-gray-500"}>
                                {row.winnerName}
                              </span>
                            </td>
                            <td className="px-2 py-1.5 md:px-3 md:py-2">
                              <span className={`inline-flex w-[14ch] items-center justify-center whitespace-normal px-1 py-0.5 rounded text-[10px] font-semibold border leading-tight text-center ${statusMeta.className}`}>
                                {statusMeta.label}
                              </span>
                              {!!statusMeta.detail && (
                                <p className="mt-1 text-[9px] text-gray-500 leading-tight">{statusMeta.detail}</p>
                              )}
                            </td>
                            <td className="px-2 py-1.5 md:px-3 md:py-2 hidden sm:table-cell whitespace-nowrap">
                              {row.payoutScheduledAt ? formatDateLong(row.payoutScheduledAt) : "-"}
                            </td>
                            <td className="px-2 py-1.5 md:px-3 md:py-2 hidden sm:table-cell whitespace-nowrap">
                              {row.payoutProcessedAt ? formatDateLong(row.payoutProcessedAt) : "-"}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!isArchivedReadOnly && pendingHistoricalInvoicePayment && (
        <div
          className="fixed inset-0 z-[130] flex items-center justify-center bg-black/45 p-4"
          onClick={closePendingHistoricalPaymentModal}
        >
          <div
            className={`w-full max-w-md rounded-2xl border bg-white p-4 shadow-xl ${
              String(pendingHistoricalInvoicePayment?.memberId || "") === String(group?.myMemberId || "")
                ? theme.border
                : "border-amber-200"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="text-sm font-semibold text-gray-900 mb-2">
              {String(pendingHistoricalInvoicePayment?.memberId || "") === String(group?.myMemberId || "")
                ? "Pay Invoice"
                : "Pay For Another Member"}
            </h4>
            <p className="text-xs text-gray-600 mb-2">
              You are about to pay for{" "}
              <span className="font-semibold text-gray-900">
                {pendingHistoricalInvoicePayment?.memberName || "this member"}
              </span>.
            </p>

            {String(pendingHistoricalInvoicePayment?.memberId || "") !== String(group?.myMemberId || "") && (
              <label className="flex items-start gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-2 mb-2">
                <input
                  type="checkbox"
                  checked={historicalProxyPaymentConfirmed}
                  onChange={(e) => setHistoricalProxyPaymentConfirmed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-400"
                />
                <span>
                  I confirm I want to pay this old-cycle invoice on behalf of{" "}
                  <span className="font-semibold text-amber-800">
                    {pendingHistoricalInvoicePayment?.memberName || "this member"}
                  </span>.
                </span>
              </label>
            )}

            <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 mb-2">
              <p className="text-[10px] uppercase tracking-wide text-slate-500">Outstanding Balance</p>
              <p className="text-sm font-semibold text-red-600">
                {formatMoney(pendingHistoricalInvoicePayment?.balanceAmount || 0)}
              </p>
            </div>

            <label className="block text-[10px] font-bold uppercase tracking-wide text-gray-600 mb-1">
              Amount To Pay
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              max={Number(pendingHistoricalInvoicePayment?.balanceAmount || 0)}
              value={historicalPaymentAmountInput}
              onChange={(e) => setHistoricalPaymentAmountInput(e.target.value)}
              className={`w-full rounded-lg border px-3 py-2 text-xs outline-none focus:ring-2 ${
                historicalPaymentValidationMessage
                  ? "border-red-300 focus:ring-red-300"
                  : `${theme.border} ${theme.ring}`
              }`}
            />
            {!!historicalPaymentValidationMessage && (
              <p className="mt-1 text-[10px] text-red-600">{historicalPaymentValidationMessage}</p>
            )}

            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={closePendingHistoricalPaymentModal}
                disabled={String(isPayingHistoricalInvoiceId || "") === String(pendingHistoricalInvoicePayment?.id || "")}
                className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmHistoricalInvoicePayment}
                disabled={
                  (String(pendingHistoricalInvoicePayment?.memberId || "") !== String(group?.myMemberId || "") &&
                    !historicalProxyPaymentConfirmed) ||
                  !!historicalPaymentValidationMessage ||
                  String(isPayingHistoricalInvoiceId || "") === String(pendingHistoricalInvoicePayment?.id || "")
                }
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60 transition-all ${theme.button}`}
              >
                {String(isPayingHistoricalInvoiceId || "") === String(pendingHistoricalInvoicePayment?.id || "")
                  ? "Processing..."
                  : "Pay"}
              </button>
            </div>
          </div>
        </div>
      )}

      {!isArchivedReadOnly && !!welfareLossDecisionModal && !!welfareLossDecisionRequest && (
        <div
          className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-slate-900/45 backdrop-blur-[1px]"
          onClick={closeWelfareLossDecisionModal}
        >
          <div
            className="w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="px-4 py-3 border-b bg-emerald-50 border-emerald-200"
            >
              <h4 className="text-sm font-bold text-gray-900">
                Verify Loss Report
              </h4>
              <p className="text-[11px] text-gray-600 mt-1">
                {welfareLossDecisionRequest?.claimantName || "Member"} |{" "}
                {welfareLossDecisionRequest?.subjectLabel || "Loss report"}
              </p>
            </div>

            <div className="px-4 py-4">
              {!!welfareLossDecisionError && (
                <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
                  {welfareLossDecisionError}
                </div>
              )}
              <label className="block text-[11px] font-semibold text-gray-700 mb-1">
                Note/Description <span className="text-red-600">* List the provided paperwork below</span>
              </label>
              <textarea
                rows={4}
                maxLength={1000}
                value={welfareLossDecisionNote}
                onChange={(e) => setWelfareLossDecisionNote(e.target.value)}
                placeholder="Write reason/details for this decision."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-xs text-gray-800 outline-none focus:ring-2 focus:ring-indigo-200"
              />
              <p className="mt-1 text-[10px] text-gray-500 text-right">
                {String(welfareLossDecisionNote || "").trim().length}/1000
              </p>
            </div>

            <div className="px-4 pb-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeWelfareLossDecisionModal}
                disabled={isUpdatingWelfareLossApproval}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitWelfareLossDecision}
                disabled={isUpdatingWelfareLossApproval || !String(welfareLossDecisionNote || "").trim()}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-60 bg-emerald-600 hover:bg-emerald-700"
              >
                {isUpdatingWelfareLossApproval
                  ? "Saving..."
                  : "Next"}
              </button>
            </div>
          </div>
        </div>
      )}

      {!isArchivedReadOnly && !!nextCycleActionModal && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-slate-900/45 backdrop-blur-[1px]">
          <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden">
            <div
              className={`px-4 py-3 border-b ${
                nextCycleActionModal.action === "leave"
                  ? "bg-red-50 border-red-200"
                  : "bg-amber-50 border-amber-200"
              }`}
            >
              <div className="flex items-center gap-2">
                <div
                  className={`w-8 h-8 rounded-lg border flex items-center justify-center ${
                    nextCycleActionModal.action === "leave"
                      ? "bg-red-100 border-red-200 text-red-700"
                      : "bg-amber-100 border-amber-200 text-amber-700"
                  }`}
                >
                  <AlertTriangle size={16} />
                </div>
	                <h4 className="text-sm font-bold text-gray-900">
	                  {nextCycleActionModal.action === "leave"
	                    ? "Confirm Leave Group"
	                    : nextCycleActionModal.action === "unskip"
	                      ? "Confirm Rejoin Next Cycle"
	                      : "Confirm Skip Next Cycle"}
	                </h4>
              </div>
            </div>

            <div className="px-4 py-4">
	              <p className="text-xs text-gray-700 leading-relaxed">
	                {nextCycleActionModal.action === "leave"
	                  ? `${nextCycleActionModal.memberName} will be marked to leave this group after the current cycle ends.`
	                  : nextCycleActionModal.action === "unskip"
	                    ? `${nextCycleActionModal.memberName} will rejoin the next cycle.`
	                    : `${nextCycleActionModal.memberName} will be skipped for the next cycle.`}
	              </p>
            </div>

            <div className="px-4 pb-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeNextCycleActionModal}
                disabled={isUpdatingNextCycleMember}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmNextCycleAction}
                disabled={isUpdatingNextCycleMember}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-60 ${
                  nextCycleActionModal.action === "leave"
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-amber-600 hover:bg-amber-700"
                }`}
	              >
	                {isUpdatingNextCycleMember
	                  ? "Processing..."
	                  : nextCycleActionModal.action === "leave"
	                    ? "Yes, Leave Group"
	                    : nextCycleActionModal.action === "unskip"
	                      ? "Rejoin"
	                      : "Yes, Skip Next Cycle"}
	              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default GroupDetails

